import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useHistory from '../hooks/useHistory';
import './slider-styles.css';

// Importar constantes modulares
import { ELEMENT_TYPES, WIDGET_CATEGORIES } from '../constants/elementTypes.js';
import { VIEWPORT_MODES, VIEWPORT_CONFIGS } from '../constants/viewportConfigs.js';
import { getResponsiveProperty } from '../utils/responsiveUtils.js';
import { WidgetRegistry } from './Elements/registry/index.js';

// Importar widgets modulares
import './Elements/base/Container/index.jsx';
import {
  FiArrowLeft,
  FiSave,
  FiEye,
  FiGlobe,
  FiSettings,
  FiType,
  FiImage,
  FiVideo,
  FiMail,
  FiGrid,
  FiColumns,
  FiSquare,
  FiEdit3,
  FiTrash2,
  FiCopy,
  FiMove,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiBold,
  FiItalic,
  FiUnderline,
  FiDroplet,
  FiPlus,
  FiMonitor,
  FiTablet,
  FiSmartphone,
  FiFolder,
  FiUpload,
  FiChevronDown,
  FiChevronUp,
  FiChevronRight,
  FiX,
  FiSearch,
  FiFilter,
  FiCheck,
  FiMoreVertical,
  FiRotateCcw,
  FiRotateCw,
  FiArrowRight,
  FiArrowDown,
  FiArrowUp,
  FiArrowUpRight,
  FiCornerDownRight,
  FiCornerUpRight,
  FiCornerLeftUp,
  FiCornerRightUp,
  FiCornerLeftDown,
  FiCornerRightDown,
  FiCircle,
  FiMinus,
  FiMinusSquare,
  FiMaximize,
  FiMaximize2,
  FiSliders,
  FiArrowLeft as FiArrowLeftIcon,
  FiLink,
  FiLink2,
} from 'react-icons/fi';


// Elementos disponibles en la sidebar
const availableElements = [
  {
    id: 'container',
    type: ELEMENT_TYPES.CONTAINER,
    name: 'Contenedor',
    icon: <FiGrid className="w-5 h-5" />,
    defaultProps: {
      layout: 'vertical', // vertical, horizontal
      gap: '16px',
      padding: '20px',
      backgroundColor: 'transparent',
      borderRadius: '0px',
      border: 'none',
      minHeight: '100px',
      alignment: 'left', // left, center, right
      children: [], // Array de elementos hijos
      // Propiedades responsives - cada viewport tiene sus propias propiedades
      responsive: {
        [VIEWPORT_MODES.DESKTOP]: {
          flexDirection: 'row', // Horizontal en escritorio
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: '24px',
          padding: '32px',
        },
        [VIEWPORT_MODES.TABLET]: {
          flexDirection: 'column', // Vertical en tablet
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          gap: '20px',
          padding: '24px',
        },
        [VIEWPORT_MODES.MOBILE]: {
          flexDirection: 'column', // Vertical en móvil
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          gap: '16px',
          padding: '16px',
        }
      },
      // Propiedades legacy para compatibilidad
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      flexWrap: 'nowrap',
      width: '1340px',
      height: '200px',
      widthType: 'full', // 'full' o 'box' - full por defecto para mejor compatibilidad con flexbox
      columnGap: '20px',
      rowGap: '20px',
    }
  },
  {
    id: 'heading',
    type: ELEMENT_TYPES.HEADING,
    name: 'Encabezado',
    icon: <FiType className="w-5 h-5" />,
    defaultProps: {
      text: 'Tu Encabezado Aquí',
      level: 1,
      alignment: 'left',
      color: '#000000',
      fontSize: '32px',
    }
  },
  {
    id: 'text',
    type: ELEMENT_TYPES.TEXT,
    name: 'Texto',
    icon: <FiType className="w-5 h-5" />,
    defaultProps: {
      text: 'Tu texto aquí. Puedes editarlo haciendo clic.',
      alignment: 'left',
      color: '#333333',
      fontSize: '16px',
    }
  },
  {
    id: 'image',
    type: ELEMENT_TYPES.IMAGE,
    name: 'Imagen',
    icon: <FiImage className="w-5 h-5" />,
    defaultProps: {
      src: '/api/placeholder/400/300',
      alt: 'Imagen',
      width: '100%',
      height: 'auto',
    }
  },
  {
    id: 'button',
    type: ELEMENT_TYPES.BUTTON,
    name: 'Botón',
    icon: <FiSquare className="w-5 h-5" />,
    defaultProps: {
      text: 'Haz Clic Aquí',
      link: '#',
      backgroundColor: '#8b5cf6',
      textColor: '#ffffff',
      padding: '12px 24px',
      borderRadius: '8px',
    }
  },
  {
    id: 'video',
    type: ELEMENT_TYPES.VIDEO,
    name: 'Video',
    icon: <FiVideo className="w-5 h-5" />,
    defaultProps: {
      src: '',
      width: '100%',
      height: '315px',
    }
  },
  {
    id: 'form',
    type: ELEMENT_TYPES.FORM,
    name: 'Formulario',
    icon: <FiMail className="w-5 h-5" />,
    defaultProps: {
      fields: ['email'],
      buttonText: 'Enviar',
      placeholder: 'Tu email...',
    }
  },
  {
    id: 'section',
    type: ELEMENT_TYPES.SECTION,
    name: 'Sección',
    icon: <FiGrid className="w-5 h-5" />,
    defaultProps: {
      backgroundColor: '#f8f9fa',
      padding: '40px 20px',
      minHeight: '200px',
    }
  },
  {
    id: 'columns',
    type: ELEMENT_TYPES.COLUMNS,
    name: 'Columnas',
    icon: <FiColumns className="w-5 h-5" />,
    defaultProps: {
      columns: 2,
      gap: '20px',
    }
  },
];

// Componente de menú contextual para contenedores
function ContainerContextMenu({ isOpen, onClose, position, element, onCopy, onPaste, onDuplicate, onDelete, canPaste = false, viewportMode, onUpdateElement }) {
  const menuRef = useRef(null);

  // Cerrar menú al hacer clic fuera o presionar ESC
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  const handleMenuItemClick = (action, e) => {
    e.stopPropagation();
    onClose();
    
    switch (action) {
      case 'copy':
        if (typeof onCopy === 'function') {
          onCopy(element);
        }
        break;
      case 'paste':
        if (typeof onPaste === 'function') {
          onPaste(element);
        }
        break;
      case 'duplicate':
        if (typeof onDuplicate === 'function') {
          onDuplicate(element);
        }
        break;
      case 'delete':
        if (typeof onDelete === 'function') {
          onDelete(element.id);
        }
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="fixed bg-[#1a1a1a] rounded-lg shadow-xl border border-[#2a2a2a] py-1 z-50 min-w-[160px]"
      style={{
        left: position.x,
        top: position.y
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Opciones básicas del menú contextual */}
      <button
        onClick={(e) => handleMenuItemClick('copy', e)}
        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-[#2a2a2a] hover:text-white transition-colors"
      >
        <FiCopy className="w-4 h-4" />
        Copiar
      </button>
      
      <button
        onClick={(e) => handleMenuItemClick('paste', e)}
        disabled={!canPaste}
        className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
          canPaste 
            ? 'text-gray-300 hover:bg-[#2a2a2a] hover:text-white' 
            : 'text-gray-500 cursor-not-allowed'
        }`}
      >
        <FiDroplet className="w-4 h-4" />
        Pegar
      </button>
      
      <button
        onClick={(e) => handleMenuItemClick('duplicate', e)}
        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-[#2a2a2a] hover:text-white transition-colors"
      >
        <FiCopy className="w-4 h-4" />
        Duplicar
      </button>
      
      <div className="border-t border-[#2a2a2a] my-1"></div>
      
      <button
        onClick={(e) => handleMenuItemClick('delete', e)}
        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-red-900/30 hover:text-red-300 transition-colors"
      >
        <FiTrash2 className="w-4 h-4" />
        Eliminar
      </button>
    </div>
  );
}

// Componente de menú de acciones para elementos
function ElementActionsMenu({ element, onCopy, onPaste, onDuplicate, onDelete, showOnHover = false, size = "md", position = "top-2 right-2", canPaste = false }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  const sizeClasses = {
    sm: {
      button: "p-1",
      icon: "w-4 h-4",
      menu: "w-40",
      menuItem: "px-3 py-2 text-xs",
      menuItemIcon: "w-3 h-3"
    },
    md: {
      button: "p-2",
      icon: "w-5 h-5", 
      menu: "w-44",
      menuItem: "px-3 py-2 text-sm",
      menuItemIcon: "w-4 h-4"
    }
  };

  const currentSizeClasses = sizeClasses[size];

  const handleToggleMenu = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = (action, e) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    
    switch (action) {
      case 'copy':
        if (typeof onCopy === 'function') {
          onCopy(element);
        }
        break;
      case 'paste':
        if (typeof onPaste === 'function') {
          onPaste(element);
        }
        break;
      case 'duplicate':
        console.log('🔄 ElementActionsMenu duplicate clicked:', {
          elementId: element.id,
          elementType: element.type,
          onDuplicateType: typeof onDuplicate,
          onDuplicateExists: !!onDuplicate,
          onDuplicateFunction: onDuplicate?.toString ? onDuplicate.toString().substring(0, 200) : 'No toString method'
        });
        if (typeof onDuplicate === 'function') {
          console.log('✅ Calling onDuplicate with element:', element);
          onDuplicate(element);
        } else {
          console.error('❌ onDuplicate is not a function:', onDuplicate);
        }
        break;
      case 'delete':
        console.log('🗑️ ElementActionsMenu delete clicked:', {
          elementId: element.id,
          elementType: element.type,
          onDeleteType: typeof onDelete,
          onDeleteFunction: onDelete.toString().substring(0, 100)
        });
        if (typeof onDelete === 'function') {
          onDelete(element.id);
        }
        break;
    }
  };

  return (
    <div 
      className={`absolute ${position} ${showOnHover ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} transition-opacity pointer-events-none`}
    >
      <div className="relative pointer-events-auto" ref={menuRef}>
        {/* Botón de menú con tres puntos verticales - adaptado al tema oscuro */}
        <button
          onClick={handleToggleMenu}
          className={`${currentSizeClasses.button} text-gray-400 hover:text-white bg-black/60 hover:bg-black/80 rounded transition-colors backdrop-blur-sm`}
          title="Opciones"
        >
          <FiMoreVertical className={`${currentSizeClasses.icon} stroke-2`} />
        </button>

        {/* Menú desplegable - adaptado al tema oscuro */}
        {isMenuOpen && (
          <div className={`absolute right-0 mt-1 ${currentSizeClasses.menu} bg-[#1a1a1a] rounded-lg shadow-xl border border-[#2a2a2a] py-1 z-10`}>
            <button
              onClick={(e) => handleMenuItemClick('copy', e)}
              className={`${currentSizeClasses.menuItem} w-full text-left hover:bg-[#2a2a2a] transition-colors flex items-center gap-2 text-gray-300 hover:text-white`}
            >
              <FiCopy className={currentSizeClasses.menuItemIcon} />
              Copiar
            </button>
            <button
              onClick={(e) => handleMenuItemClick('paste', e)}
              disabled={!canPaste}
              className={`${currentSizeClasses.menuItem} w-full text-left hover:bg-[#2a2a2a] transition-colors flex items-center gap-2 ${
                canPaste 
                  ? 'text-gray-300 hover:text-white' 
                  : 'text-gray-500 cursor-not-allowed'
              }`}
            >
              <FiDroplet className={currentSizeClasses.menuItemIcon} />
              Pegar
            </button>
            <button
              onClick={(e) => handleMenuItemClick('duplicate', e)}
              className={`${currentSizeClasses.menuItem} w-full text-left hover:bg-[#2a2a2a] transition-colors flex items-center gap-2 text-gray-300 hover:text-white`}
            >
              <FiCopy className={currentSizeClasses.menuItemIcon} />
              Duplicar
            </button>
            <div className="border-t border-[#2a2a2a] my-1"></div>
            <button
              onClick={(e) => handleMenuItemClick('delete', e)}
              className={`${currentSizeClasses.menuItem} w-full text-left hover:bg-red-900/30 transition-colors flex items-center gap-2 text-red-400 hover:text-red-300`}
            >
              <FiTrash2 className={currentSizeClasses.menuItemIcon} />
              Eliminar
            </button>
          </div>
        )}
      </div>
      
    </div>
  );
}

// Componente para renderizar elementos hijo dentro de contenedores
function ContainerChild({ element, onSelect, onDelete, onDuplicate, isSelected, onAddToContainer, onMoveToContainer, selectedElement, viewportMode, parentElement, onUpdateElement }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 } });
  const childRef = useRef(null);

  // Cerrar menú contextual al hacer clic fuera o presionar ESC
  useEffect(() => {
    function handleClickOutside(event) {
      if (childRef.current && !childRef.current.contains(event.target)) {
        setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
      }
    }

    if (contextMenu.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [contextMenu.isOpen]);

  const handleDragStart = (e) => {
    console.log('🚀 ContainerChild drag started:', element.id, element.type);
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'canvas-element', id: element.id }));
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };

  const handleDragEnd = (e) => {
    console.log('🏁 ContainerChild drag ended:', element.id);
    setIsDragging(false);
    e.stopPropagation();
  };
  
  // Manejar clic derecho para mostrar menú contextual
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🖱️ ContainerChild context menu triggered for element:', element.id, element.type);
    
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      position: {
        x: e.clientX,
        y: e.clientY
      }
    });
  };
  
  const renderChildElement = () => {
    switch (element.type) {
      case ELEMENT_TYPES.CONTAINER:
        // Determinar si debe centrarse (igual que el contenedor padre en el lienzo)
        const shouldCenter = element.props.widthType === 'box' && element.props.width && element.props.width !== '100%';
        
        // Verificar si debe usar borde degradado
        const hasBorderGradient = (() => {
          const borderColorMode = element.props.borderColorMode || 'solid';
          const borderWidth = parseInt(element.props.borderWidth || '0');
          const shouldShowBorder = isSelected || (!element.props.children || element.props.children.length === 0);
          
          console.log('🔍 ContainerChild Border gradient check:', {
            elementId: element.id,
            borderColorMode,
            borderWidth,
            shouldShowBorder,
            borderGradientColor1: element.props.borderGradientColor1,
            borderGradientColor2: element.props.borderGradientColor2
          });
          
          return !isDragOver && shouldShowBorder && borderColorMode === 'gradient' && borderWidth > 0;
        })();

        const containerChildElement = hasBorderGradient ? (
          // Wrapper para borde degradado que respeta border-radius
          <div
            style={{
              width: (() => {
                if (element.props.widthType === 'full') return '100%';
                
                // Obtener el ancho del hijo
                const childWidth = element.props.width || '1340px';
                
                // Si no hay padre, usar el ancho del hijo
                if (!parentElement) return childWidth;
                
                // Calcular el ancho disponible del padre
                const parentWidth = parentElement.props.widthType === 'full' ? '100%' : parentElement.props.width || '1340px';
                
                // Si el padre es 100%, el hijo puede usar su ancho normal
                if (parentWidth === '100%') return childWidth;
                
                // Convertir anchos a números para comparar
                const childWidthNum = parseInt(childWidth.toString().replace('px', '')) || 1340;
                const parentWidthNum = parseInt(parentWidth.toString().replace('px', '')) || 1340;
                
                // Obtener el padding del padre para calcular el espacio disponible
                const parentPadding = parentElement.props.padding || '20px';
                const paddingNum = parseInt(parentPadding.toString().replace('px', '')) || 20;
                const availableWidth = parentWidthNum - (paddingNum * 2); // padding left + right
                
                // El hijo no puede ser más ancho que el espacio disponible del padre
                return childWidthNum > availableWidth ? `${availableWidth}px` : childWidth;
              })(),
              height: 'auto',
              minHeight: element.props.height && element.props.height !== 'auto' 
                ? element.props.height 
                : (element.props.minHeight || '100px'),
              padding: `${element.props.borderWidth || '1'}px`,
              borderRadius: element.props.borderRadius,
              background: (() => {
                // Construir el degradado correctamente a partir de las propiedades
                const color1 = element.props.borderGradientColor1 || '#ff1b6d';
                const color2 = element.props.borderGradientColor2 || '#8b5cf6';
                const direction = element.props.borderGradientDirection || '0deg';
                return `linear-gradient(${direction}, ${color1}, ${color2})`;
              })(),
              position: 'relative',
              flexShrink: 0, // Evitar que se encoja
              boxSizing: 'border-box'
            }}
          >
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsDragOver(false);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                
                console.log('🎯 ContainerChild onDrop triggered for container:', element.id);
                
                try {
                  const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                  console.log('📦 Drag data:', data);
                  
                  if (data.type === 'panel-element') {
                    console.log('✅ Adding element to nested container:', element.id, 'Element:', data.element.name);
                    onAddToContainer(element.id, data.element);
                  } else if (data.type === 'canvas-element') {
                    console.log('✅ Moving EXISTING element to nested container:', element.id, 'Element ID:', data.id);
                    onMoveToContainer && onMoveToContainer(data.id, element.id);
                  } else {
                    console.log('❌ Drag data type not recognized:', data.type);
                  }
                } catch (error) {
                  console.error('❌ Error parsing drag data:', error);
                }
              }}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'row',
                gap: getResponsiveProperty(element.props, 'gap', viewportMode) || element.props.gap,
                padding: getResponsiveProperty(element.props, 'padding', viewportMode) || element.props.padding,
                backgroundColor: element.props.glassEffect 
                  ? `${element.props.glassColor || '#ffffff'}${Math.round((element.props.glassOpacity || 20) * 2.55).toString(16).padStart(2, '0')}` 
                  : element.props.backgroundColor,
                backgroundImage: element.props.backgroundImage || 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
                WebkitBackdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
                borderRadius: (() => {
                  const radius = element.props.borderRadius || '0px';
                  const borderWidth = parseInt(element.props.borderWidth || '1');
                  // Reducir el radio interno para compensar el borde
                  const radiusValue = parseInt(radius.replace('px', '')) || 0;
                  const innerRadius = Math.max(0, radiusValue - borderWidth);
                  return `${innerRadius}px`;
                })(),
                border: 'none',
                alignItems: getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch',
                justifyContent: getResponsiveProperty(element.props, 'justifyContent', viewportMode) || element.props.justifyContent || 'flex-start',
                flexWrap: getResponsiveProperty(element.props, 'flexWrap', viewportMode) || element.props.flexWrap || 'wrap',
                boxSizing: 'border-box',
                overflow: 'visible',
                flexShrink: 0,
              }}
              className={isDragOver ? 'bg-gray-50' : ''}
            >
              {element.props.children && element.props.children.length > 0 ? (
                element.props.children.map((child, childIndex) => {
                  const childWrapperClasses = (() => {
                    const parentDirection = getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'row';
                    const maxHeight = '100%';
                    const maxWidth = '100%';
                    const flexGrow = 1;
                    const flexBasis = 'auto';
                    const flexClasses = `flex-${flexGrow} min-w-0 basis-${flexBasis}`;
                    const parentAlignItems = getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch';

                    if (parentDirection === 'row' || parentDirection === 'row-reverse') {
                      return 'flex-1 min-w-0 relative';
                    }

                    let baseClasses = 'w-full flex-1';

                    return baseClasses;
                  })();

                  return (
                    <div key={child.id} className={childWrapperClasses}>
                      <ContainerChild
                        element={child}
                        onSelect={onSelect}
                        onDelete={(childId) => onDelete(childId, element.id)}
                        onDuplicate={(childElement) => onDuplicate(childElement, element.id)}
                        isSelected={selectedElement?.id === child.id}
                        onAddToContainer={onAddToContainer}
                        onMoveToContainer={onMoveToContainer}
                        selectedElement={selectedElement}
                        viewportMode={viewportMode}
                        parentElement={element}
                        onUpdateElement={onUpdateElement}
                      />
                    </div>
                  );
                })
              ) : (
                <div className={`text-center py-8 transition-colors ${
                  isDragOver ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <FiGrid className={`w-8 h-8 mx-auto mb-2 opacity-50 ${
                    isDragOver ? 'text-gray-600' : ''
                  }`} />
                  <p className="text-sm">
                    {isDragOver ? '¡Suelta aquí para agregar!' : 'Contenedor vacío'}
                  </p>
                  <p className="text-xs">
                    {isDragOver ? 'Elemento listo para agregarse' : 'Arrastra elementos aquí'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOver(true);
            }}
            onDragLeave={(e) => {
              e.stopPropagation();
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsDragOver(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOver(false);
              
              console.log('🎯 ContainerChild onDrop triggered for container:', element.id);
              
              try {
                const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                console.log('📦 Drag data:', data);
                
                if (data.type === 'panel-element') {
                  console.log('✅ Adding element to nested container:', element.id, 'Element:', data.element.name);
                  onAddToContainer(element.id, data.element);
                } else if (data.type === 'canvas-element') {
                  console.log('✅ Moving EXISTING element to nested container:', element.id, 'Element ID:', data.id);
                  onMoveToContainer && onMoveToContainer(data.id, element.id);
                } else {
                  console.log('❌ Drag data type not recognized:', data.type);
                }
              } catch (error) {
                console.error('❌ Error parsing drag data:', error);
              }
            }}
            style={{
              // El contenedor hijo debe respetar el ancho del contenedor padre
              width: (() => {
                if (element.props.widthType === 'full') return '100%';
                
                // Obtener el ancho del padre y del hijo
                const childWidth = element.props.width || '1340px';
                
                // Si no hay padre, usar el ancho del hijo
                if (!parentElement) return childWidth;
                
                // Calcular el ancho disponible del padre
                const parentWidth = parentElement.props.widthType === 'full' ? '100%' : parentElement.props.width || '1340px';
                
                // Si el padre es 100%, el hijo puede usar su ancho normal
                if (parentWidth === '100%') return childWidth;
                
                // Convertir anchos a números para comparar
                const childWidthNum = parseInt(childWidth.toString().replace('px', '')) || 1340;
                const parentWidthNum = parseInt(parentWidth.toString().replace('px', '')) || 1340;
                
                // Obtener el padding del padre para calcular el espacio disponible
                const parentPadding = parentElement.props.padding || '20px';
                const paddingNum = parseInt(parentPadding.toString().replace('px', '')) || 20;
                const availableWidth = parentWidthNum - (paddingNum * 2); // padding left + right
                
                // El hijo no puede ser más ancho que el espacio disponible del padre
                return childWidthNum > availableWidth ? `${availableWidth}px` : childWidth;
              })(),
              // Altura automática para que se ajuste a los hijos
              height: 'auto',
              // Usar minHeight solo como altura mínima, no como altura fija
              minHeight: element.props.height && element.props.height !== 'auto' 
                ? element.props.height 
                : (element.props.minHeight || '100px'),
              display: 'flex',
flexDirection: getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'row',
              gap: getResponsiveProperty(element.props, 'gap', viewportMode) || element.props.gap,
              padding: getResponsiveProperty(element.props, 'padding', viewportMode) || element.props.padding,
              backgroundColor: element.props.glassEffect 
                ? `${element.props.glassColor || '#ffffff'}${Math.round((element.props.glassOpacity || 20) * 2.55).toString(16).padStart(2, '0')}` 
                : element.props.backgroundColor,
              backgroundImage: element.props.backgroundImage || 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
              WebkitBackdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
              borderRadius: element.props.borderRadius,
              border: isDragOver ? '2px dashed #6b7280' : 
                      (isSelected || (!element.props.children || element.props.children.length === 0)) ? 
                      element.props.border : 'none',
              alignItems: getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch',
              justifyContent: getResponsiveProperty(element.props, 'justifyContent', viewportMode) || element.props.justifyContent || 'flex-start',
              flexWrap: getResponsiveProperty(element.props, 'flexWrap', viewportMode) || element.props.flexWrap || 'wrap',
              boxSizing: 'border-box',
              // Permitir overflow visible para que el padre crezca con el contenido
              overflow: 'visible',
              // Asegurar que el contenedor se comporte como un bloque flexible
              flexShrink: 0, // No encogerse
            }}
            className={isDragOver ? 'bg-gray-50' : ''}
          >
            {element.props.children && element.props.children.length > 0 ? (
              element.props.children.map((child, childIndex) => {
                // Determinar clases CSS basadas en la configuración del contenedor padre
                const childWrapperClasses = (() => {
                  const parentDirection = getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'row';
                  const parentAlignItems = getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch';
                  
                  console.log('🔍 Parent direction for child wrapper:', {
                    parentDirection,
                    elementId: element.id,
                    childId: child.id,
                    responsiveValue: getResponsiveProperty(element.props, 'flexDirection', viewportMode),
                    directValue: element.props.flexDirection
                  });
                  
                  // Para direcciones de fila: usar flex-1 para distribución equitativa con bordes claros
                  if (parentDirection === 'row' || parentDirection === 'row-reverse') {
                    return 'flex-1 min-w-0 relative';
                  }
                  
                  // Para direcciones de columna: aplicar alineación horizontal basada en alignItems
                  let baseClasses = 'w-full';
                  
                  // En dirección de columna, alignItems controla la alineación horizontal
                  if (parentAlignItems === 'center') {
                    baseClasses = 'w-full flex justify-center';
                  } else if (parentAlignItems === 'flex-end') {
                    baseClasses = 'w-full flex justify-end';
                  } else if (parentAlignItems === 'flex-start') {
                    baseClasses = 'w-full flex justify-start';
                  }
                  
                  return baseClasses;
                })();
                
                return (
                  <div key={child.id} className={childWrapperClasses}>
                    <ContainerChild
                      element={child}
                      onSelect={onSelect}
                      onDelete={(childId) => onDelete(childId, element.id)}
                      onDuplicate={(childElement) => onDuplicate(childElement, element.id)}
                      isSelected={selectedElement?.id === child.id}
                      onAddToContainer={onAddToContainer}
                      onMoveToContainer={onMoveToContainer}
                      selectedElement={selectedElement}
                      viewportMode={viewportMode}
                      parentElement={element}
                      onUpdateElement={onUpdateElement}
                    />
                  </div>
                );
              })
            ) : (
              <div className={`text-center py-4 transition-colors ${
isDragOver ? 'text-gray-600' : 'text-gray-400'
              }`}>
                <FiGrid className={`w-6 h-6 mx-auto mb-2 opacity-50 ${
isDragOver ? 'text-gray-600' : ''
                }`} />
                <p className="text-xs">
                  {isDragOver ? 'Suelta aquí' : 'Contenedor anidado'}
                </p>
              </div>
            )}
          </div>
        );
        
        // Si debe centrarse, envolver en un div centrador pero preservando el ancho exacto
        if (shouldCenter && element.props.widthType === 'box') {
          return (
            <div className="w-full flex justify-center">
              <div style={{ width: element.props.width || '1340px', flexShrink: 0 }}>
                {containerChildElement}
              </div>
            </div>
          );
        }
        return containerChildElement;
      case ELEMENT_TYPES.HEADING:
        return (
          <div
            style={{
              color: element.props.color,
              fontSize: element.props.fontSize,
              textAlign: element.props.alignment,
            }}
          >
            <h1>{element.props.text}</h1>
          </div>
        );
      case ELEMENT_TYPES.TEXT:
        return (
          <div
            style={{
              color: element.props.color,
              fontSize: element.props.fontSize,
              textAlign: element.props.alignment,
            }}
          >
            {element.props.text}
          </div>
        );
      case ELEMENT_TYPES.IMAGE:
        return (
          <img
            src={element.props.src}
            alt={element.props.alt}
            style={{
              width: element.props.width,
              height: element.props.height,
              objectFit: 'cover',
            }}
          />
        );
      case ELEMENT_TYPES.BUTTON:
        return (
          <button
            style={{
              backgroundColor: element.props.backgroundColor,
              backgroundImage: element.props.backgroundImage || 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              color: element.props.textColor,
              padding: element.props.padding,
              borderRadius: element.props.borderRadius,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {element.props.text}
          </button>
        );
      default:
        return <div className="p-2 bg-gray-100 rounded text-center text-sm text-gray-500">Elemento: {element.type}</div>;
    }
  };

  // Only make non-container elements draggable as containers handle their own dragging
  const shouldBeDraggable = element.type !== ELEMENT_TYPES.CONTAINER;

  return (
    <div
      ref={childRef}
      draggable={shouldBeDraggable}
      onDragStart={shouldBeDraggable ? handleDragStart : undefined}
      onDragEnd={shouldBeDraggable ? handleDragEnd : undefined}
      className={`relative group/item ${isSelected ? 'ring-2 ring-[#8b5cf6]' : ''} ${
        isDragging ? 'opacity-70' : ''
      } hover:ring-2 hover:ring-[#8b5cf6] transition-all`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(element);
      }}
    >
      {renderChildElement()}
      
      {/* Overlay de herramientas para elementos hijo */}
      <div className="absolute top-1 right-1 opacity-0 group-hover/item:opacity-100 transition-opacity pointer-events-none">
        <div className="pointer-events-auto">
          <ElementActionsMenu
            element={element}
            onDuplicate={(childElement) => {
              console.log('🔄 ContainerChild onDuplicate called:', {
                childElementId: childElement.id,
                childElementType: childElement.type,
                parentElementId: parentElement.id,
                parentElementType: parentElement.type
              });
              onDuplicate(childElement, parentElement.id);
            }}
            onDelete={(elementId) => onDelete(elementId, parentElement.id)}
            showOnHover={false}
            size="sm"
            position="relative"
          />
        </div>
      </div>
      
      {/* Menú contextual para elementos hijo */}
      <ContainerContextMenu
        isOpen={contextMenu.isOpen}
        onClose={() => setContextMenu({ isOpen: false, position: { x: 0, y: 0 } })}
        position={contextMenu.position}
        element={element}
        viewportMode={viewportMode}
        onUpdateElement={onUpdateElement}
        onCopy={(el) => {
          console.log('📋 Copying child element:', el.id);
          navigator.clipboard.writeText(JSON.stringify(el)).then(() => {
            console.log('✅ Child element copied to clipboard');
          }).catch(err => {
            console.error('❌ Failed to copy child element:', err);
          });
        }}
        onPaste={(el) => {
          console.log('📄 Pasting to child element:', el.id);
          // Para elementos hijo, pegar significa agregar al contenedor padre
          navigator.clipboard.readText().then(text => {
            try {
              const copiedElement = JSON.parse(text);
              const newElement = {
                ...copiedElement,
                id: `${copiedElement.type}-${Date.now()}`
              };
              onAddToContainer(parentElement.id, newElement);
              console.log('✅ Element pasted successfully to parent container');
            } catch (err) {
              console.error('❌ Failed to paste element:', err);
            }
          }).catch(err => {
            console.error('❌ Failed to read clipboard:', err);
          });
        }}
        onDuplicate={(el) => {
          console.log('🔄 ContainerChild context menu duplicate:', {
            elementId: el.id,
            elementType: el.type,
            parentElementId: parentElement.id,
            parentElementType: parentElement.type
          });
          onDuplicate(el, parentElement.id);
        }}
        onDelete={(elId) => {
          console.log('🗑️ ContainerChild context menu delete:', elId);
          onDelete(elId, parentElement.id);
        }}
        canPaste={false} // TODO: Implementar verificación de portapapeles
      />
      
    </div>
  );
}

// Componente para renderizar elementos del canvas
function CanvasElement({ element, index, isSelected, onSelect, onDelete, onDuplicate, onReorder, onAddToContainer, onMoveToContainer, selectedElement, viewportMode, onUpdateElement }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 } });
  const dragRef = useRef(null);

  // Cerrar menú contextual al hacer clic fuera o presionar ESC
  useEffect(() => {
    function handleClickOutside(event) {
      if (dragRef.current && !dragRef.current.contains(event.target)) {
        setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
      }
    }

    if (contextMenu.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [contextMenu.isOpen]);

  const renderElement = () => {
    switch (element.type) {
      case ELEMENT_TYPES.CONTAINER:
        // Determinar si debe centrarse (modo 'box' y ancho específico)
        const shouldCenter = element.props.widthType === 'box' || (element.props.widthType !== 'full' && element.props.width && element.props.width !== '100%');
        
        console.log('🔍 Container centering check:', {
          elementId: element.id,
          widthType: element.props.widthType,
          width: element.props.width,
          shouldCenter
        });
        
        // Verificar si debe usar borde degradado
        const hasBorderGradient = (() => {
          const borderColorMode = element.props.borderColorMode || 'solid';
          const borderWidth = parseInt(element.props.borderWidth || '0');
          const shouldShowBorder = isSelected || (!element.props.children || element.props.children.length === 0);
          
          console.log('🔍 CanvasElement Border gradient check:', {
            elementId: element.id,
            borderColorMode,
            borderWidth,
            shouldShowBorder,
            borderGradientColor1: element.props.borderGradientColor1,
            borderGradientColor2: element.props.borderGradientColor2
          });
          
          return !isDragOver && shouldShowBorder && borderColorMode === 'gradient' && borderWidth > 0;
        })();

        const containerElement = hasBorderGradient ? (
          // Wrapper para borde degradado que respeta border-radius
          <div
            style={{
              width: element.props.widthType === 'full' ? '100%' : element.props.width || '1340px',
              height: 'auto',
              minHeight: element.props.height && element.props.height !== 'auto' 
                ? element.props.height 
                : (element.props.minHeight || '100px'),
              padding: `${element.props.borderWidth || '1'}px`,
              borderRadius: element.props.borderRadius,
              background: (() => {
                // Construir el degradado correctamente a partir de las propiedades
                const color1 = element.props.borderGradientColor1 || '#ff1b6d';
                const color2 = element.props.borderGradientColor2 || '#8b5cf6';
                const direction = element.props.borderGradientDirection || '0deg';
                return `linear-gradient(${direction}, ${color1}, ${color2})`;
              })(),
              position: 'relative',
              flexShrink: 0, // Evitar que se encoja
              boxSizing: 'border-box'
            }}
          >
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsDragOver(false);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                
                console.log('🎯 CanvasElement Container onDrop triggered for:', element.id);
                
                try {
                  const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                  console.log('📦 Canvas Container drag data:', data);
                  
                  if (data.type === 'panel-element') {
                    console.log('✅ Adding NEW element to main container:', element.id, 'Element:', data.element.name);
                    onAddToContainer(element.id, data.element);
                  } else if (data.type === 'canvas-element') {
                    console.log('✅ Moving EXISTING element to main container:', element.id, 'Element ID:', data.id);
                    onMoveToContainer && onMoveToContainer(data.id, element.id);
                  } else {
                    console.log('❌ Canvas Container drag data type not recognized:', data.type);
                  }
                } catch (error) {
                  console.error('❌ Error parsing canvas container drag data:', error);
                }
              }}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'column',
                gap: getResponsiveProperty(element.props, 'gap', viewportMode) || element.props.gap,
                paddingTop: getResponsiveProperty(element.props, 'paddingTop', viewportMode) || element.props.paddingTop || element.props.padding || '0px',
                paddingRight: getResponsiveProperty(element.props, 'paddingRight', viewportMode) || element.props.paddingRight || element.props.padding || '0px',
                paddingBottom: getResponsiveProperty(element.props, 'paddingBottom', viewportMode) || element.props.paddingBottom || element.props.padding || '0px',
                paddingLeft: getResponsiveProperty(element.props, 'paddingLeft', viewportMode) || element.props.paddingLeft || element.props.padding || '0px',
                backgroundColor: element.props.glassEffect 
                  ? `${element.props.glassColor || '#ffffff'}${Math.round((element.props.glassOpacity || 20) * 2.55).toString(16).padStart(2, '0')}` 
                  : element.props.backgroundColor,
                backgroundImage: element.props.backgroundImage || 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
                WebkitBackdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
                borderRadius: (() => {
                  const radius = element.props.borderRadius || '0px';
                  const borderWidth = parseInt(element.props.borderWidth || '1');
                  // Reducir el radio interno para compensar el borde
                  const radiusValue = parseInt(radius.replace('px', '')) || 0;
                  const innerRadius = Math.max(0, radiusValue - borderWidth);
                  return `${innerRadius}px`;
                })(),
                border: 'none',
                alignItems: getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch',
                justifyContent: getResponsiveProperty(element.props, 'justifyContent', viewportMode) || element.props.justifyContent || 'flex-start',
                flexWrap: getResponsiveProperty(element.props, 'flexWrap', viewportMode) || element.props.flexWrap || 'wrap',
                boxSizing: 'border-box',
                overflow: 'visible',
                flexShrink: 0,
              }}
              className={isDragOver ? 'bg-gray-50' : ''}
            >
              {element.props.children && element.props.children.length > 0 ? (
                element.props.children.map((child, childIndex) => {
                  const childWrapperClasses = (() => {
                    const parentDirection = getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'row';
                    const maxHeight = '100%';
                    const maxWidth = '100%';
                    const flexGrow = 1;
                    const flexBasis = 'auto';
                    const flexClasses = `flex-${flexGrow} min-w-0 basis-${flexBasis}`;
                    const parentAlignItems = getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch';

                    if (parentDirection === 'row' || parentDirection === 'row-reverse') {
                      return 'flex-1 min-w-0 relative';
                    }

                    let baseClasses = 'w-full flex-1';

                    return baseClasses;
                  })();

                  return (
                    <div key={child.id} className={childWrapperClasses}>
                    <ContainerChild
                        element={child}
                        onSelect={onSelect}
                        onDelete={(childId) => onDelete(childId, element.id)}
                        onDuplicate={(childElement) => onDuplicate(childElement, element.id)}
                        isSelected={selectedElement?.id === child.id}
                        onAddToContainer={onAddToContainer}
                        onMoveToContainer={onMoveToContainer}
                        selectedElement={selectedElement}
                        viewportMode={viewportMode}
                        parentElement={element}
                        onUpdateElement={onUpdateElement}
                      />
                    </div>
                  );
                })
              ) : (
                <div className={`text-center py-8 transition-colors ${
                  isDragOver ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <FiGrid className={`w-8 h-8 mx-auto mb-2 opacity-50 ${
                    isDragOver ? 'text-gray-600' : ''
                  }`} />
                  <p className="text-sm">
                    {isDragOver ? '¡Suelta aquí para agregar!' : 'Contenedor vacío'}
                  </p>
                  <p className="text-xs">
                    {isDragOver ? 'Elemento listo para agregarse' : 'Arrastra elementos aquí'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOver(true);
            }}
            onDragLeave={(e) => {
              e.stopPropagation();
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsDragOver(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOver(false);
              
              console.log('🎯 CanvasElement Container onDrop triggered for:', element.id);
              
              try {
                const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                console.log('📦 Canvas Container drag data:', data);
                
                if (data.type === 'panel-element') {
                  console.log('✅ Adding NEW element to main container:', element.id, 'Element:', data.element.name);
                  onAddToContainer(element.id, data.element);
                } else if (data.type === 'canvas-element') {
                  console.log('✅ Moving EXISTING element to main container:', element.id, 'Element ID:', data.id);
                  onMoveToContainer && onMoveToContainer(data.id, element.id);
                } else {
                  console.log('❌ Canvas Container drag data type not recognized:', data.type);
                }
              } catch (error) {
                console.error('❌ Error parsing canvas container drag data:', error);
              }
            }}
            style={{
              width: element.props.widthType === 'full' ? '100%' : element.props.width || '1340px',
              // El contenedor padre debe ajustarse automáticamente al contenido de sus hijos
              height: 'auto', // Siempre automático para que se ajuste a los hijos
              // Usar minHeight solo como altura mínima, no como altura fija
              minHeight: element.props.height && element.props.height !== 'auto' 
                ? element.props.height 
                : (element.props.minHeight || '100px'),
              display: 'flex',
              flexDirection: getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'column',
              gap: getResponsiveProperty(element.props, 'gap', viewportMode) || element.props.gap,
              paddingTop: getResponsiveProperty(element.props, 'paddingTop', viewportMode) || element.props.paddingTop || element.props.padding || '0px',
              paddingRight: getResponsiveProperty(element.props, 'paddingRight', viewportMode) || element.props.paddingRight || element.props.padding || '0px',
              paddingBottom: getResponsiveProperty(element.props, 'paddingBottom', viewportMode) || element.props.paddingBottom || element.props.padding || '0px',
              paddingLeft: getResponsiveProperty(element.props, 'paddingLeft', viewportMode) || element.props.paddingLeft || element.props.padding || '0px',
              backgroundColor: element.props.glassEffect 
                ? `${element.props.glassColor || '#ffffff'}${Math.round((element.props.glassOpacity || 20) * 2.55).toString(16).padStart(2, '0')}` 
                : element.props.backgroundColor,
              backgroundImage: element.props.backgroundImage || 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
              WebkitBackdropFilter: element.props.glassEffect ? `blur(${element.props.glassBlur || 10}px)` : 'none',
              borderRadius: element.props.borderRadius,
              border: isDragOver ? '2px dashed #6b7280' : 
                      (isSelected || (!element.props.children || element.props.children.length === 0)) ? 
                      element.props.border : 'none',
              alignItems: getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch',
              justifyContent: getResponsiveProperty(element.props, 'justifyContent', viewportMode) || element.props.justifyContent || 'flex-start',
              flexWrap: getResponsiveProperty(element.props, 'flexWrap', viewportMode) || element.props.flexWrap || 'wrap',
              boxSizing: 'border-box',
              // Permitir overflow visible para que el padre crezca con el contenido
              overflow: 'visible',
              // Asegurar que el contenedor se comporte como un bloque flexible
              flexShrink: 0, // No encogerse
            }}
            className={isDragOver ? 'bg-gray-50' : ''}
          >
            {element.props.children && element.props.children.length > 0 ? (
              element.props.children.map((child, childIndex) => {
                // Determinar clases CSS basadas en la configuración del contenedor padre (igual que en ContainerChild)
                const childWrapperClasses = (() => {
                  const parentDirection = getResponsiveProperty(element.props, 'flexDirection', viewportMode) || element.props.flexDirection || 'row';
                  const maxHeight = '100%'; // Maximum height setup
                  const maxWidth = '100%'; // Prevent overflow on x-axis
                  const flexGrow = 1;
                  const flexBasis = 'auto'; // Auto basis ensures consistent sizing
                  const flexClasses = `flex-${flexGrow} min-w-0 basis-${flexBasis}`;
                  const parentAlignItems = getResponsiveProperty(element.props, 'alignItems', viewportMode) || element.props.alignItems || 'stretch';
                  
                  // Para direcciones de fila: usar flex-1 para distribución equitativa con bordes claros
                  if (parentDirection === 'row' || parentDirection === 'row-reverse') {
                    return 'flex-1 min-w-0 relative'; // Added relative positioning
                  }
                  
                  // Para direcciones de columna: usar w-full y flex-1 para distribución vertical equitativa
                  let baseClasses = 'w-full flex-1';
                  
                  // En dirección de columna, NO aplicar wrappers flex adicionales
                  // para que los elementos se estiren completamente
                  
                  return baseClasses;
                })();
                
                return (
                  <div key={child.id} className={childWrapperClasses}>
                    <ContainerChild
                      element={child}
                      onSelect={onSelect}
                      onDelete={(childId) => onDelete(childId, element.id)}
                      onDuplicate={(childElement) => onDuplicate(childElement, element.id)}
                      isSelected={selectedElement?.id === child.id}
                      onAddToContainer={onAddToContainer}
                      onMoveToContainer={onMoveToContainer}
                      selectedElement={selectedElement}
                      viewportMode={viewportMode}
                      parentElement={element}
                      onUpdateElement={onUpdateElement}
                    />
                  </div>
                );
              })
            ) : (
              <div className={`text-center py-8 transition-colors ${
isDragOver ? 'text-gray-600' : 'text-gray-400'
              }`}>
                <FiGrid className={`w-8 h-8 mx-auto mb-2 opacity-50 ${
isDragOver ? 'text-gray-600' : ''
                }`} />
                <p className="text-sm">
                  {isDragOver ? '¡Suelta aquí para agregar!' : 'Contenedor vacío'}
                </p>
                <p className="text-xs">
                  {isDragOver ? 'Elemento listo para agregarse' : 'Arrastra elementos aquí'}
                </p>
              </div>
            )}
          </div>
        );
        
        // Si debe centrarse, envolver en un div centrador pero preservando el ancho exacto
        if (shouldCenter && element.props.widthType === 'box') {
          return (
            <div className="w-full flex justify-center">
              <div style={{ width: element.props.width || '1340px', flexShrink: 0 }}>
                {containerElement}
              </div>
            </div>
          );
        }
        return containerElement;
      case ELEMENT_TYPES.HEADING:
        return (
          <div
            style={{
              color: element.props.color,
              fontSize: element.props.fontSize,
              textAlign: element.props.alignment,
            }}
          >
            <h1>{element.props.text}</h1>
          </div>
        );
      case ELEMENT_TYPES.TEXT:
        return (
          <div
            style={{
              color: element.props.color,
              fontSize: element.props.fontSize,
              textAlign: element.props.alignment,
            }}
          >
            {element.props.text}
          </div>
        );
      case ELEMENT_TYPES.IMAGE:
        return (
          <img
            src={element.props.src}
            alt={element.props.alt}
            style={{
              width: element.props.width,
              height: element.props.height,
              objectFit: 'cover',
            }}
          />
        );
      case ELEMENT_TYPES.BUTTON:
        return (
          <button
            style={{
              backgroundColor: element.props.backgroundColor,
              backgroundImage: element.props.backgroundImage || 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              color: element.props.textColor,
              padding: element.props.padding,
              borderRadius: element.props.borderRadius,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {element.props.text}
          </button>
        );
      case ELEMENT_TYPES.SECTION:
        return (
          <div
            style={{
              backgroundColor: element.props.backgroundColor,
              backgroundImage: element.props.backgroundImage || 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              padding: element.props.padding,
              minHeight: element.props.minHeight,
              border: '2px dashed #4a5568',
            }}
          >
            <p className="text-gray-400 text-center">Sección - Arrastra elementos aquí</p>
          </div>
        );
      default:
        return <div className="p-4 bg-gray-700 rounded">Elemento: {element.type}</div>;
    }
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'canvas-element', id: element.id, index }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (data.type === 'canvas-element' && data.id !== element.id) {
      onReorder(data.index, index);
    }
  };

  // Los contenedores no deben ser draggable en el wrapper principal para evitar conflictos con drop
  const isDraggableWrapper = element.type !== ELEMENT_TYPES.CONTAINER;
  
  // Handle separado para arrastrar contenedores
  const handleContainerDragStart = (e) => {
    console.log('🎯 Container drag started:', element.id);
    e.stopPropagation();
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'canvas-element', id: element.id, index }));
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleContainerDragEnd = (e) => {
    e.stopPropagation();
    setIsDragging(false);
  };


  // Manejar clic derecho para mostrar menú contextual
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🖱️ CanvasElement context menu triggered for element:', element.id, element.type);
    
    setContextMenu({
      isOpen: true,
      position: {
        x: e.clientX,
        y: e.clientY
      }
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
  };

  return (
    <div
      ref={dragRef}
      draggable={isDraggableWrapper}
      onDragStart={isDraggableWrapper ? handleDragStart : undefined}
      onDragEnd={isDraggableWrapper ? handleDragEnd : undefined}
      onDragOver={isDraggableWrapper ? handleDragOver : undefined}
      onDrop={isDraggableWrapper ? handleDrop : undefined}
      onContextMenu={handleContextMenu}
      className={`relative group ${isSelected ? 'ring-2 ring-[#8b5cf6]' : ''} ${
        isDragging ? 'opacity-70' : ''
      } hover:ring-2 hover:ring-[#8b5cf6] transition-all`}
      onClick={() => onSelect(element)}
    >
      <div className="relative">
        {renderElement()}
        
        {/* Overlay de herramientas que aparece al hacer hover */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-2 left-2 pointer-events-auto">
            {/* Handle de mover para todos los elementos, incluyendo contenedores */}
            {element.type === ELEMENT_TYPES.CONTAINER ? (
              <div
                draggable
                onDragStart={handleContainerDragStart}
                onDragEnd={handleContainerDragEnd}
                className="p-1 bg-[#8b5cf6] text-white rounded hover:bg-[#7c3aed] transition-all cursor-grab active:cursor-grabbing opacity-80 hover:opacity-100"
                title="Mover contenedor"
              >
                <FiMove className="w-2.5 h-2.5" />
              </div>
            ) : (
              <div
                className="p-1 bg-[#8b5cf6] text-white rounded hover:bg-[#7c3aed] transition-all cursor-grab active:cursor-grabbing opacity-80 hover:opacity-100"
                title="Mover"
              >
                <FiMove className="w-2.5 h-2.5" />
              </div>
            )}
          </div>
          
          {/* Menú de acciones usando ElementActionsMenu */}
          <ElementActionsMenu
            element={element}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            showOnHover={true}
            size="md"
            position="top-2 right-2"
          />
        </div>
      </div>
      
      {/* Menú contextual */}
      <ContainerContextMenu
        isOpen={contextMenu.isOpen}
        onClose={handleCloseContextMenu}
        position={contextMenu.position}
        element={element}
        viewportMode={viewportMode}
        onUpdateElement={onUpdateElement}
        onCopy={(el) => {
          console.log('📋 Copying element:', el.id);
          navigator.clipboard.writeText(JSON.stringify(el)).then(() => {
            console.log('✅ Element copied to clipboard');
          }).catch(err => {
            console.error('❌ Failed to copy element:', err);
          });
        }}
        onPaste={(el) => {
          console.log('📄 Pasting to element:', el.id);
          navigator.clipboard.readText().then(text => {
            try {
              const copiedElement = JSON.parse(text);
              const newElement = {
                ...copiedElement,
                id: `${copiedElement.type}-${Date.now()}`
              };
              // Para elementos del canvas, agregar como nuevo elemento
              onAddElement && onAddElement(newElement);
              console.log('✅ Element pasted successfully');
            } catch (err) {
              console.error('❌ Failed to paste element:', err);
            }
          }).catch(err => {
            console.error('❌ Failed to read clipboard:', err);
          });
        }}
        onDuplicate={onDuplicate}
        onDelete={(elId) => {
          console.log('🗑️ CanvasElement context menu delete:', elId);
          onDelete(elId);
        }}
        canPaste={false} // TODO: Implementar verificación de portapapeles
      />
    </div>
  );
}

// Componente para elemento arrastrables del panel
function PanelElement({ element, onClick }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      onClick(e);
    }
  };

  const handleDragStart = (e) => {
    console.log('🚀 PanelElement drag started:', element.name);
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'panel-element', element: element }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      className={`flex flex-col items-center justify-center p-4 bg-[#2a2a2a] rounded-lg text-white hover:bg-[#3a3a3a] transition-colors min-h-[80px] group cursor-grab active:cursor-grabbing select-none ${
        isDragging ? 'opacity-50' : ''
      }`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
    >
      <div 
        className="mb-2 group-hover:scale-110 transition-transform pointer-events-none" 
        style={{ userSelect: 'none' }}
      >
        {element.icon}
      </div>
      <span 
        className="text-xs text-center font-medium pointer-events-none" 
        style={{ userSelect: 'none' }}
      >
        {element.name}
      </span>
    </div>
  );
}

// Componente Canvas Infinito con scroll REAL
function InfiniteCanvas({ elements, selectedElement, onSelectElement, onDeleteElement, onDuplicateElement, onAddElement, onReorderElements, onAddToContainer, onMoveToContainer, viewportMode }) {
  const scrollContainerRef = useRef(null);
  const [virtualHeight, setVirtualHeight] = useState(5000); // Altura virtual inicial
  const [isDragOver, setIsDragOver] = useState(false);

  // Expandir el área virtual cuando sea necesario
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      
      // Si estamos cerca del final (80%), agregar más espacio
      if (scrollTop + clientHeight > scrollHeight * 0.8) {
        setVirtualHeight(prev => prev + 2000);
      }
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      
      if (data.type === 'panel-element') {
        onAddElement(data.element);
      } else if (data.type === 'canvas-element') {
        onMoveToContainer && onMoveToContainer(data.id, null);
      }
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  };

  return (
    <div className="w-full h-full bg-gray-100">
      <div
        ref={scrollContainerRef}
        className="w-full h-full overflow-x-hidden" 
        style={{ overflowY: virtualHeight > (scrollContainerRef.current?.clientHeight || 0) ? 'auto' : 'hidden' }}
        onScroll={handleScroll}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Contenedor virtual que puede crecer infinitamente */}
        <div 
          style={{ height: `${virtualHeight}px` }}
          className="relative"
        >
          <Canvas 
            elements={elements} 
            selectedElement={selectedElement} 
            onSelectElement={onSelectElement} 
            onDeleteElement={onDeleteElement} 
            onDuplicateElement={onDuplicateElement} 
            onAddElement={onAddElement}
            onReorderElements={onReorderElements}
            onAddToContainer={onAddToContainer}
            onMoveToContainer={onMoveToContainer}
            viewportMode={viewportMode}
            isDragOver={isDragOver}
            containerPadding="20px"
          />
        </div>
      </div>
    </div>
  );
}

// Componente Canvas optimizado para scroll infinito
function Canvas({ elements, selectedElement, onSelectElement, onDeleteElement, onDuplicateElement, onAddElement, onReorderElements, onAddToContainer, onMoveToContainer, viewportMode, isDragOver = false, containerPadding = "0px" }) {

  // Configuración de viewport actual
  const viewportConfig = VIEWPORT_CONFIGS[viewportMode];

  return (
    <div 
      className="w-full flex justify-center" 
      style={{ padding: containerPadding }}
    >
      <div
        style={{
          width: viewportConfig.width,
          maxWidth: viewportConfig.maxWidth,
          minHeight: elements.length === 0 ? viewportConfig.minHeight : 'auto',
          padding: viewportConfig.padding,
          boxSizing: 'border-box',
          marginBottom: '1000px', // Espacio extra al final
        }}
        className={`bg-white transition-colors ${
          isDragOver ? 'bg-blue-50 ring-2 ring-blue-300' : ''
        } ${viewportMode === VIEWPORT_MODES.DESKTOP ? 'w-full' : 'rounded-lg shadow-lg border border-gray-200'} ${
          viewportMode === VIEWPORT_MODES.MOBILE ? 'safe-area-inset' : ''
        }`}
      >
      {elements.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[500px]">
          {/* Área principal de bienvenida */}
          <div className={`text-center transition-all duration-300 ${
            isDragOver 
              ? 'transform scale-105 text-blue-600' 
              : 'text-gray-500'
          }`}>
            {/* Ícono principal animado */}
            <div className={`relative mb-6 ${
              isDragOver ? 'animate-pulse' : ''
            }`}>
              <div className={`w-20 h-20 mx-auto rounded-full border-2 border-dashed flex items-center justify-center transition-all duration-300 ${
                isDragOver 
                  ? 'border-blue-400 bg-blue-50 shadow-lg' 
                  : 'border-gray-300 bg-gray-50'
              }`}>
                {isDragOver ? (
                  <FiPlus className="w-8 h-8 text-blue-500" />
                ) : (
                  <FiGrid className="w-8 h-8 text-gray-400" />
                )}
              </div>
              
              {/* Indicador de arrastrar */}
              {isDragOver && (
                <div className="absolute -inset-2 border-2 border-blue-300 rounded-full animate-ping opacity-75"></div>
              )}
            </div>
            
            {/* Títulos dinámicos */}
            <h2 className={`text-2xl font-bold mb-3 transition-all duration-300 ${
              isDragOver ? 'text-blue-600' : 'text-gray-700'
            }`}>
              {isDragOver ? '¡Perfecto! Suelta aquí' : 'Comienza tu diseño'}
            </h2>
            
            <p className={`text-base mb-8 max-w-md mx-auto leading-relaxed transition-all duration-300 ${
              isDragOver ? 'text-blue-500' : 'text-gray-500'
            }`}>
              {isDragOver 
                ? 'Tu elemento será agregado al lienzo y podrás editarlo inmediatamente' 
                : 'Elige una plantilla, crea una estructura personalizada o arrastra elementos desde el panel lateral para empezar'}
            </p>
            
            {/* Indicadores de ayuda visual cuando no está en drag over */}
            {!isDragOver && (
              <div className="flex items-center justify-center gap-8 mb-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <FiFolder className="w-4 h-4" />
                  <span>Plantillas</span>
                </div>
                <div className="w-px h-4 bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <FiGrid className="w-4 h-4" />
                  <span>Estructuras</span>
                </div>
                <div className="w-px h-4 bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <FiType className="w-4 h-4" />
                  <span>Elementos</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Sistema de plantillas con mejor espaciado */}
          <div className={`w-full transition-all duration-300 ${
            isDragOver ? 'opacity-30 transform scale-95' : 'opacity-100'
          }`}>
            <CanvasTemplateSystem 
              onAddContainerStructure={onAddElement}
              onLoadTemplate={() => console.log('Cargar plantilla de biblioteca')}
              onUploadTemplate={() => console.log('Subir plantilla JSON')}
            />
          </div>
        </div>
      ) : (
        <>
          {elements.map((element, index) => (
            <CanvasElement
              key={element.id}
              element={element}
              index={index}
              isSelected={selectedElement?.id === element.id}
              onSelect={onSelectElement}
              onDelete={onDeleteElement}
              onDuplicate={onDuplicateElement}
              onReorder={onReorderElements}
              onAddToContainer={onAddToContainer}
              onMoveToContainer={onMoveToContainer}
              selectedElement={selectedElement}
              viewportMode={viewportMode}
            />
          ))}
          {/* Sistema de plantillas y estructuras predeterminadas */}
          <CanvasTemplateSystem 
            onAddContainerStructure={onAddElement}
            onLoadTemplate={() => console.log('Cargar plantilla de biblioteca')}
            onUploadTemplate={() => console.log('Subir plantilla JSON')}
          />
          
        </>
      )}
      </div>
    </div>
  );
}

// Panel de elementos disponibles
function ElementsPanel({ onAddElement }) {
  const handleElementClick = (element, e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddElement(element);
  };

  return (
    <div className="w-64 bg-[#1a1a1a] border-r border-[#2a2a2a] p-4">
      <h3 className="text-white font-semibold mb-4">Elementos</h3>
      <div className="text-xs text-gray-400 mb-3">Arrastra o haz clic para agregar</div>
      <div className="grid grid-cols-2 gap-3">
        {availableElements.map((element) => (
          <div key={element.id} className="relative">
            <PanelElement 
              element={element} 
              onClick={(e) => handleElementClick(element, e)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Panel de propiedades
function PropertiesPanel({ selectedElement, onUpdateElement, viewportMode }) {
  const [activeTab, setActiveTab] = useState('layout'); // 'layout', 'style' o 'advanced'
  const [localWidth, setLocalWidth] = useState(1340);
  const [localHeight, setLocalHeight] = useState(200);
  const [isPaddingLinked, setIsPaddingLinked] = useState(false); // Estado del enlace de padding
  
  // Estados para acordeones de la pestaña de estilo
  const [styleAccordions, setStyleAccordions] = useState({
    background: true, // Fondo abierto por defecto
    border: false,    // Borde cerrado por defecto
  });

  // Sincronizar estado local cuando cambia el elemento seleccionado
  useEffect(() => {
    if (selectedElement) {
      const width = selectedElement.props.width || '1340px';
      const height = selectedElement.props.height || '200px';
      const widthNum = parseInt(width.toString().replace('px', '')) || 1340;
      const heightNum = parseInt(height.toString().replace('px', '')) || 200;
      setLocalWidth(widthNum);
      setLocalHeight(heightNum);
      console.log('🔄 Syncing local state:', { width, height, widthNum, heightNum });
    }
  }, [selectedElement]);

  if (!selectedElement) {
    return (
      <div className="w-80 bg-[#1a1a1a] border-l border-[#2a2a2a] p-6">
        <p className="text-gray-400 text-sm">Selecciona un elemento para editar sus propiedades</p>
      </div>
    );
  }

  const handlePropertyChange = (property, value) => {
    console.log('🔥 handlePropertyChange called:', { 
      property, 
      value, 
      elementType: selectedElement.type,
      elementId: selectedElement.id,
      currentProps: selectedElement.props
    });
    
    const updatedElement = {
      ...selectedElement,
      props: {
        ...selectedElement.props,
        [property]: value,
      },
    };
    
    console.log('🔄 Updated element:', updatedElement);
    
    // Para contenedores, también actualizar la propiedad responsiva correspondiente al viewport actual
    if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
      updatedElement.props.responsive = {
        ...selectedElement.props.responsive,
        [viewportMode]: {
          ...selectedElement.props.responsive[viewportMode],
          [property]: value
        }
      };
      console.log('📱 Updated responsive props:', updatedElement.props.responsive);
    }
    
    // Para contenedores, preservar explícitamente los children
    if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
      updatedElement.props.children = selectedElement.props.children;
    }
    
    console.log('✅ Calling onUpdateElement with:', updatedElement);
    onUpdateElement(updatedElement);
  };

  // Función para obtener las propiedades de disposición por tipo de elemento
  const getLayoutProperties = () => {
    switch (selectedElement.type) {
      case ELEMENT_TYPES.CONTAINER:
        return (
          <div className="space-y-6">
            {/* Espaciado entre elementos - movido desde la pestaña Avanzado */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Espaciado entre elementos</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={parseInt(selectedElement.props.gap)}
                  onChange={(e) => handlePropertyChange('gap', `${e.target.value}px`)}
                  style={{
                    '--progress': `${(parseInt(selectedElement.props.gap) / 50) * 100}%`
                  }}
                  className="flex-1 slider-custom"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={parseInt(selectedElement.props.gap)}
                  onChange={(e) => handlePropertyChange('gap', `${e.target.value}px`)}
                  className="w-20 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm"
                  title="Espaciado en píxeles"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap min-w-[40px]">px</span>
              </div>
            </div>
            
            {/* Control de tipo de ancho */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Tipo de Ancho</label>
              <select
                value={selectedElement.props.widthType || 'full'}
                onChange={(e) => {
                  const newWidthType = e.target.value;
                  
                  // Crear el elemento actualizado de una vez con todas las propiedades necesarias
                  const updatedElement = {
                    ...selectedElement,
                    props: {
                      ...selectedElement.props,
                      widthType: newWidthType,
                      width: newWidthType === 'full' ? '100%' : 
                             (newWidthType === 'box' && (!selectedElement.props.width || selectedElement.props.width === '100%')) ? 
                             `${localWidth}px` : selectedElement.props.width
                    }
                  };
                  
                  // Para contenedores, también actualizar la propiedad responsiva correspondiente al viewport actual
                  if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                    updatedElement.props.responsive = {
                      ...selectedElement.props.responsive,
                      [viewportMode]: {
                        ...selectedElement.props.responsive[viewportMode],
                        widthType: newWidthType
                      }
                    };
                  }
                  
                  // Para contenedores, preservar explícitamente los children
                  if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                    updatedElement.props.children = selectedElement.props.children;
                  }
                  
                  console.log('🔄 Width type changed to:', newWidthType, 'Updated element:', updatedElement);
                  onUpdateElement(updatedElement);
                }}
                className="w-full px-3 py-1.5 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d]"
              >
                <option value="full">Ancho Completo</option>
                <option value="box">Caja (Personalizado)</option>
              </select>
            </div>
            {/* Control de ancho - solo visible en modo 'box' */}
            {(selectedElement.props.widthType || 'full') === 'box' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Ancho Personalizado</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="300"
                    max="1340"
                    value={localWidth}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      console.log('🎚️ Width slider changed to:', newValue);
                      setLocalWidth(newValue);
                      handlePropertyChange('width', `${newValue}px`);
                    }}
                    style={{
                      '--progress': `${((localWidth - 300) / (1340 - 300)) * 100}%`
                    }}
                    className="flex-1 slider-custom"
                  />
                  
                  <input
                    type="number"
                    min="300"
                    max="1340"
                    value={localWidth}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value) || 300;
                      console.log('🔢 Width input changed to:', newValue);
                      setLocalWidth(newValue);
                      handlePropertyChange('width', `${newValue}px`);
                    }}
                    className="w-20 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm"
                    title="Ancho en píxeles"
                  />
                  <span className="text-xs text-gray-400 whitespace-nowrap min-w-[40px]">px</span>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Alto Personalizado</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="1000"
                  value={localHeight}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    console.log('📱 Height slider changed to:', newValue);
                    setLocalHeight(newValue);
                    handlePropertyChange('height', `${newValue}px`);
                  }}
                  style={{
                    '--progress': `${(localHeight / 1000) * 100}%`
                  }}
                  className="flex-1 slider-custom"
                />
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={localHeight}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 0;
                    console.log('🔢 Height input changed to:', newValue);
                    setLocalHeight(newValue);
                    handlePropertyChange('height', `${newValue}px`);
                  }}
                  className="w-20 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm"
                  title="Alto en píxeles"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap min-w-[40px]">px</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Dirección</label>
              <div className="flex gap-2">
                {[
                  { value: 'row', label: 'Fila', icon: <FiArrowRight className="w-4 h-4 mx-auto" /> },
                  { value: 'column', label: 'Columna', icon: <FiArrowDown className="w-4 h-4 mx-auto" /> },
                  { value: 'row-reverse', label: 'Fila Invertida', icon: <FiArrowLeft className="w-4 h-4 mx-auto" /> },
                  { value: 'column-reverse', label: 'Columna Invertida', icon: <FiArrowUp className="w-4 h-4 mx-auto" /> }
                ].map((layout) => (
                  <button
                    key={layout.value}
                    onClick={() => handlePropertyChange('flexDirection', layout.value)}
                    className={`flex-1 p-3 rounded ${
                      selectedElement.props.flexDirection === layout.value
                        ? 'bg-[#1f1f1f] text-gray-100'
                        : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                    }`}
                    title={layout.label}
                  >
                    {layout.icon}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Contenido Justificado</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'flex-start', icon: <FiAlignLeft className="w-4 h-4 mx-auto" /> },
                  { value: 'center', icon: <FiAlignCenter className="w-4 h-4 mx-auto" /> },
                  { value: 'flex-end', icon: <FiAlignRight className="w-4 h-4 mx-auto" /> },
                  { value: 'space-between', icon: <FiMinus className="w-4 h-4 mx-auto" /> },
                  { value: 'space-around', icon: <FiMaximize className="w-4 h-4 mx-auto" /> },
                  { value: 'space-evenly', icon: <FiMaximize2 className="w-4 h-4 mx-auto" /> }
                ].map((justify) => (
                  <button
                    key={justify.value}
                    onClick={() => handlePropertyChange('justifyContent', justify.value)}
                    className={`p-2 rounded text-xs ${
                      selectedElement.props.justifyContent === justify.value
                        ? 'bg-[#1f1f1f] text-gray-100'
                        : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      {justify.icon}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Alinear Elemento</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'stretch', icon: <FiMaximize className="w-4 h-4 mx-auto" /> },
                  { value: 'flex-start', icon: <FiArrowUp className="w-4 h-4 mx-auto" /> },
                  { value: 'center', icon: <FiMinus className="w-4 h-4 mx-auto" /> },
                  { value: 'flex-end', icon: <FiArrowDown className="w-4 h-4 mx-auto" /> },
                  { value: 'baseline', icon: <FiMinusSquare className="w-4 h-4 mx-auto" /> }
                ].map((align) => (
                  <button
                    key={align.value}
                    onClick={() => handlePropertyChange('alignItems', align.value)}
                    className={`p-2 rounded text-xs ${
                      selectedElement.props.alignItems === align.value
                        ? 'bg-[#1f1f1f] text-gray-100'
                        : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      {align.icon}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Ajuste de Línea</label>
              <div className="flex gap-2">
                {[
                  { value: 'nowrap', icon: <FiArrowRight className="w-4 h-4 mx-auto" /> },
                  { value: 'wrap', icon: <FiCornerDownRight className="w-4 h-4 mx-auto" /> },
                  { value: 'wrap-reverse', icon: <FiCornerUpRight className="w-4 h-4 mx-auto" /> }
                ].map((wrap) => (
                  <button
                    key={wrap.value}
                    onClick={() => handlePropertyChange('flexWrap', wrap.value)}
                    className={`flex-1 p-3 rounded ${
                      selectedElement.props.flexWrap === wrap.value
                        ? 'bg-[#1f1f1f] text-gray-100'
                        : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      {wrap.icon}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case ELEMENT_TYPES.HEADING:
      case ELEMENT_TYPES.TEXT:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Alineación</label>
              <div className="flex gap-1">
                {['left', 'center', 'right'].map((align) => (
                  <button
                    key={align}
                    onClick={() => handlePropertyChange('alignment', align)}
                    className={`flex-1 p-2 rounded ${
                      selectedElement.props.alignment === align
                        ? 'bg-[#1f1f1f] text-gray-100'
                        : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                    }`}
                  >
                    {align === 'left' && <FiAlignLeft className="w-4 h-4 mx-auto" />}
                    {align === 'center' && <FiAlignCenter className="w-4 h-4 mx-auto" />}
                    {align === 'right' && <FiAlignRight className="w-4 h-4 mx-auto" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case ELEMENT_TYPES.BUTTON:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Padding</label>
              <input
                type="text"
                value={selectedElement.props.padding}
                onChange={(e) => handlePropertyChange('padding', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white text-sm"
                placeholder="12px 24px"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Radio del borde</label>
              <input
                type="text"
                value={selectedElement.props.borderRadius}
                onChange={(e) => handlePropertyChange('borderRadius', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white text-sm"
                placeholder="8px"
              />
            </div>
          </div>
        );
      default:
        return <p className="text-gray-400 text-sm">No hay propiedades de disposición para este elemento</p>;
    }
  };

  // Función para obtener las propiedades de estilo por tipo de elemento
  const getStyleProperties = () => {
    switch (selectedElement.type) {
      case ELEMENT_TYPES.CONTAINER:
        // Determinar el modo de fondo actual
        const currentBackgroundMode = (() => {
          console.log('🔍 Calculating currentBackgroundMode:', {
            elementId: selectedElement.id,
            backgroundMode: selectedElement.props.backgroundMode,
            glassEffect: selectedElement.props.glassEffect,
            backgroundImage: selectedElement.props.backgroundImage,
            backgroundColor: selectedElement.props.backgroundColor,
            allProps: selectedElement.props
          });
          
          // Si backgroundMode está establecido explícitamente, usarlo como prioridad
          if (selectedElement.props.backgroundMode) {
            console.log('✅ Using explicit backgroundMode:', selectedElement.props.backgroundMode);
            return selectedElement.props.backgroundMode;
          }
          
          // Si no hay backgroundMode explícito, inferir del estado actual
          if (selectedElement.props.glassEffect) {
            console.log('✅ Inferring glass mode from glassEffect');
            return 'glass';
          }
          if (selectedElement.props.backgroundImage && selectedElement.props.backgroundImage !== 'none') {
            // Verificar si es degradado
            if (selectedElement.props.backgroundImage.includes('linear-gradient')) {
              console.log('✅ Inferring gradient mode from backgroundImage');
              return 'gradient';
            }
            console.log('✅ Inferring image mode from backgroundImage');
            return 'image';
          }
          console.log('✅ Defaulting to color mode');
          return 'color';
        })();
        
        console.log('🎯 Final currentBackgroundMode:', currentBackgroundMode);
        
        return (
          <div className="space-y-6">
            {/* Acordeón de Fondo */}
            <div>
              <button
                onClick={() => setStyleAccordions(prev => ({ ...prev, background: !prev.background }))}
                className="w-full flex items-center justify-between py-2 hover:bg-[#2a2a2a] rounded transition-colors"
              >
                <span className="text-sm font-medium text-gray-300">
                  Fondo
                </span>
                <FiChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                  styleAccordions.background ? 'rotate-90' : ''
                }`} />
              </button>
              
              {styleAccordions.background && (
                <div className="mt-4 space-y-4">
                  {/* Pestañas para tipos de fondo */}
                  <div className="flex rounded-lg overflow-hidden border border-[#3a3a3a]">
                    {[
                      { 
                        mode: 'color', 
                        icon: <FiDroplet className="w-4 h-4" />, 
                        title: 'Color sólido'
                      },
                      { 
                        mode: 'image', 
                        icon: <FiImage className="w-4 h-4" />, 
                        title: 'Imagen de fondo'
                      },
                      { 
                        mode: 'gradient', 
                        icon: <FiFilter className="w-4 h-4" />, 
                        title: 'Degradado'
                      },
                      { 
                        mode: 'glass', 
                        icon: <FiGrid className="w-4 h-4" />, 
                        title: 'Efecto Glass'
                      }
                    ].map((backgroundType) => (
                      <button
                        key={backgroundType.mode}
                        onClick={(e) => {
                          console.log('🎯 Background type clicked:', backgroundType.mode);
                          console.log('📋 Current selected element:', selectedElement);
                          console.log('🖱️ Click event:', e);
                          
                          // Prevenir propagación y comportamiento por defecto
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Preparar todas las actualizaciones en un solo objeto
                          const updates = {
                            backgroundMode: backgroundType.mode
                          };
                          
                          if (backgroundType.mode === 'color') {
                            updates.glassEffect = false;
                            updates.backgroundImage = 'none';
                            // Por defecto usar transparente
                            if (!selectedElement.props.backgroundColor || selectedElement.props.backgroundColor === 'undefined') {
                              updates.backgroundColor = 'transparent';
                            }
                          } else if (backgroundType.mode === 'image') {
                            updates.glassEffect = false;
                            updates.backgroundColor = 'transparent';
                          } else if (backgroundType.mode === 'gradient') {
                            updates.glassEffect = false;
                            updates.backgroundColor = 'transparent';
                            // Si no hay colores de degradado, establecer valores por defecto
                            if (!selectedElement.props.gradientColor1) updates.gradientColor1 = '#ffffff';
                            if (!selectedElement.props.gradientColor2) updates.gradientColor2 = '#000000';
                          } else if (backgroundType.mode === 'glass') {
                            updates.glassEffect = true;
                            updates.backgroundColor = 'transparent';
                            updates.backgroundImage = 'none';
                            if (!selectedElement.props.glassColor) updates.glassColor = '#ffffff';
                            if (!selectedElement.props.glassOpacity) updates.glassOpacity = 20;
                            if (!selectedElement.props.glassBlur) updates.glassBlur = 10;
                          }
                          
                          console.log('🚀 Applying all updates at once:', updates);
                          
                          // Aplicar todas las actualizaciones en una sola operación
                          const updatedElement = {
                            ...selectedElement,
                            props: {
                              ...selectedElement.props,
                              ...updates, // Aplicar todas las actualizaciones de una vez
                            },
                          };
                          
                          console.log('🔄 Final updated element:', updatedElement);
                          
                          // Para contenedores, también actualizar la propiedad responsiva correspondiente al viewport actual
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                            Object.keys(updates).forEach(property => {
                              updatedElement.props.responsive = {
                                ...updatedElement.props.responsive,
                                [viewportMode]: {
                                  ...updatedElement.props.responsive[viewportMode],
                                  [property]: updates[property]
                                }
                              };
                            });
                          }
                          
                          // Para contenedores, preservar explícitamente los children
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                            updatedElement.props.children = selectedElement.props.children;
                          }
                          
                          console.log('✅ Calling onUpdateElement with final element:', updatedElement);
                          onUpdateElement(updatedElement);
                        }}
                        className={`flex-1 p-3 border-r border-[#3a3a3a] last:border-r-0 transition-colors ${
                          currentBackgroundMode === backgroundType.mode
                            ? 'bg-[#404040] text-gray-100'
                            : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                        }`}
                        title={backgroundType.title}
                      >
                        {backgroundType.icon}
                      </button>
                    ))}
                  </div>

                  {/* Contenido específico según el modo de fondo */}
                  {currentBackgroundMode === 'color' && (
                    <div className="space-y-3">
                      {/* Color picker circular en línea con código hexadecimal */}
                      <div className="flex items-center gap-3">
                        <div className="relative w-12 h-12 flex-shrink-0">
                          {/* Círculo de fondo que muestra el color actual */}
                          <div 
                            className="w-full h-full rounded-full border-2 border-[#3a3a3a] overflow-hidden relative"
                            style={{
                              backgroundColor: selectedElement.props.backgroundColor === 'transparent' ? '#ffffff' : selectedElement.props.backgroundColor || '#ffffff',
                              backgroundImage: selectedElement.props.backgroundColor === 'transparent' ? 
                                'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 
                                'none',
                              backgroundSize: selectedElement.props.backgroundColor === 'transparent' ? '8px 8px' : 'auto',
                              backgroundPosition: selectedElement.props.backgroundColor === 'transparent' ? '0 0, 0 4px, 4px -4px, -4px 0px' : 'auto'
                            }}
                          >
                            {/* Input color invisible que cubre todo el círculo */}
                            <input
                              type="color"
                              value={selectedElement.props.backgroundColor === 'transparent' ? '#ffffff' : selectedElement.props.backgroundColor || '#ffffff'}
                              onChange={(e) => handlePropertyChange('backgroundColor', e.target.value)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                              style={{
                                border: 'none',
                                outline: 'none',
                                background: 'transparent'
                              }}
                              title="Selecciona un color"
                            />
                          </div>
                        </div>
                        <input
                          type="text"
                          value={selectedElement.props.backgroundColor === 'transparent' ? 'transparent' : selectedElement.props.backgroundColor || 'transparent'}
                          onChange={(e) => {
                            const value = e.target.value.trim().toLowerCase();
                            if (value === 'transparent' || value === '') {
                              handlePropertyChange('backgroundColor', 'transparent');
                            } else if (value.startsWith('#')) {
                              handlePropertyChange('backgroundColor', value);
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value.trim().toLowerCase();
                            // Validar al perder el foco
                            if (value === 'transparent') {
                              handlePropertyChange('backgroundColor', 'transparent');
                            } else if (value && !value.match(/^#[0-9A-Fa-f]{6}$/)) {
                              // Si no es válido, restaurar valor por defecto
                              handlePropertyChange('backgroundColor', 'transparent');
                            }
                          }}
                          onKeyDown={(e) => {
                            // Permitir teclas de control (backspace, delete, arrows, etc.)
                            const controlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
                            if (controlKeys.includes(e.key)) {
                              return;
                            }
                            
                            const value = e.target.value.trim().toLowerCase();
                            if (value === 'transparen' && e.key === 't') {
                              // Permitir escribir 'transparent'
                              return;
                            }
                            
                            // Si está vacío y no es #, agregar # automáticamente
                            if (e.target.value === '' && e.key !== '#' && e.key !== 't') {
                              e.target.value = '#';
                            }
                            
                            // Solo permitir caracteres hexadecimales después del # o escribir transparent
                            if (e.target.value.length >= 1 && !value.startsWith('transparen') && !/[0-9A-Fa-f]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                          placeholder="#ffffff o transparent"
                          maxLength={11}
                        />
                      </div>
                    </div>
                  )}

                  {currentBackgroundMode === 'image' && (
                    <div className="space-y-4">
                      {/* Área de arrastrar y soltar con vista previa */}
                      <div 
                        className="relative border-2 border-dashed border-[#3a3a3a] rounded-lg p-6 transition-all duration-200 hover:border-[#ff1b6d] hover:bg-[#2a2a2a]/30"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files && files[0] && files[0].type.startsWith('image/')) {
                            const file = files[0];
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              console.log('🖼️ Image loaded via drag and drop');
                              handlePropertyChange('backgroundImage', `url(${reader.result})`);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      >
                        {/* Vista previa de la imagen actual */}
                        {selectedElement.props.backgroundImage && 
                         selectedElement.props.backgroundImage !== 'none' && 
                         !selectedElement.props.backgroundImage.includes('linear-gradient') ? (
                          <div className="relative">
                            <div 
                              className="w-full h-32 rounded-lg bg-cover bg-center bg-no-repeat border border-[#404040]"
                              style={{
                                backgroundImage: selectedElement.props.backgroundImage
                              }}
                            ></div>
                            <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('🗑️ Removing background image');
                                  handlePropertyChange('backgroundImage', 'none');
                                }}
                                className="px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
                              >
                                <FiTrash2 className="w-3 h-3" />
                                Eliminar
                              </button>
                            </div>
                            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                              Imagen de fondo
                            </div>
                          </div>
                        ) : (
                          /* Área de carga cuando no hay imagen */
                          <div className="text-center">
                            <FiImage className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                            <p className="text-gray-400 text-sm mb-2">Arrastra una imagen aquí</p>
                            <p className="text-gray-500 text-xs mb-4">o haz clic para seleccionar</p>
                            
                            <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff1b6d] text-white rounded-lg cursor-pointer hover:bg-[#e0195f] transition-colors text-sm font-medium">
                              <FiUpload className="w-4 h-4" />
                              Seleccionar Imagen
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    console.log('🖼️ Image selected via file input:', file.name);
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                      console.log('✅ Image loaded successfully');
                                      handlePropertyChange('backgroundImage', `url(${reader.result})`);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                                className="hidden"
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      
                      {/* URL de imagen manual */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-2">O ingresa una URL de imagen</label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            placeholder="https://ejemplo.com/imagen.jpg"
                            className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d]"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const url = e.target.value.trim();
                                if (url) {
                                  console.log('🌐 Setting background image from URL:', url);
                                  handlePropertyChange('backgroundImage', `url(${url})`);
                                  e.target.value = '';
                                }
                              }
                            }}
                          />
                          <button
                            onClick={(e) => {
                              const input = e.target.parentElement.querySelector('input');
                              const url = input.value.trim();
                              if (url) {
                                console.log('🌐 Setting background image from URL:', url);
                                handlePropertyChange('backgroundImage', `url(${url})`);
                                input.value = '';
                              }
                            }}
                            className="px-3 py-2 bg-[#ff1b6d] text-white rounded hover:bg-[#e0195f] transition-colors text-sm"
                          >
                            <FiCheck className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {currentBackgroundMode === 'gradient' && (
                    <div className="space-y-4">
                      {/* Vista previa del degradado */}
                      <div className="relative">
                        <div 
                          className="w-full h-32 rounded-lg border border-[#404040] transition-all duration-300"
                          style={{
                            backgroundImage: (() => {
                              const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                              const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                              const type = selectedElement.props.gradientType || 'linear';
                              const direction = selectedElement.props.gradientDirection || '135deg';
                              
                              if (type === 'radial') {
                                const position = direction === 'center' ? 'circle at center' :
                                               direction === 'top-left' ? 'circle at top left' :
                                               direction === 'top-right' ? 'circle at top right' :
                                               direction === 'bottom-left' ? 'circle at bottom left' :
                                               direction === 'bottom-right' ? 'circle at bottom right' :
                                               'circle at center';
                                return `radial-gradient(${position}, ${color1}, ${color2})`;
                              } else {
                                return `linear-gradient(${direction}, ${color1}, ${color2})`;
                              }
                            })()
                          }}
                        ></div>
                        <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                          {selectedElement.props.gradientType === 'radial' ? 'Degradado Radial' : 'Degradado Lineal'}
                        </div>
                      </div>
                      
                      {/* Selectores de color circulares - Disposición vertical */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs text-gray-400 mb-2">Color inicial</label>
                          <div className="flex items-center gap-3">
                            <div className="relative w-8 h-8 flex-shrink-0">
                              <div 
                                className="w-full h-full rounded-full border-2 border-[#3a3a3a] overflow-hidden relative"
                                style={{
                                  backgroundColor: selectedElement.props.gradientColor1 || '#ff1b6d'
                                }}
                              >
                                <input
                                  type="color"
                                  value={selectedElement.props.gradientColor1 || '#ff1b6d'}
                                  onChange={(e) => {
                                    console.log('🎨 Gradient color 1 changed to:', e.target.value);
                                    handlePropertyChange('gradientColor1', e.target.value);
                                    // Auto-aplicar degradado al cambiar color
                                    setTimeout(() => {
                                      const color1 = e.target.value;
                                      const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                                      const type = selectedElement.props.gradientType || 'linear';
                                      const direction = selectedElement.props.gradientDirection || '135deg';
                                      
                                      let gradient;
                                      if (type === 'radial') {
                                        const position = direction === 'center' ? 'circle at center' :
                                                       direction === 'top-left' ? 'circle at top left' :
                                                       direction === 'top-right' ? 'circle at top right' :
                                                       direction === 'bottom-left' ? 'circle at bottom left' :
                                                       direction === 'bottom-right' ? 'circle at bottom right' :
                                                       'circle at center';
                                        gradient = `radial-gradient(${position}, ${color1}, ${color2})`;
                                      } else {
                                        gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                      }
                                      
                                      console.log('🚀 Auto-applying gradient:', gradient);
                                      handlePropertyChange('backgroundImage', gradient);
                                    }, 10);
                                  }}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                                  style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent'
                                  }}
                                  title="Selecciona el color inicial"
                                />
                              </div>
                            </div>
                            <input
                              type="text"
                              value={selectedElement.props.gradientColor1 || '#ff1b6d'}
                              onChange={(e) => {
                                const value = e.target.value;
                                console.log('✏️ Gradient color 1 hex changed to:', value);
                                
                                // Crear el elemento actualizado con el nuevo color
                                const updatedElement = {
                                  ...selectedElement,
                                  props: {
                                    ...selectedElement.props,
                                    gradientColor1: value,
                                  },
                                };
                                
                                // Auto-aplicar degradado solo si es un color válido
                                if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                  const color1 = value;
                                  const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                                  const type = selectedElement.props.gradientType || 'linear';
                                  const direction = selectedElement.props.gradientDirection || '135deg';
                                  
                                  let gradient;
                                  if (type === 'radial') {
                                    const position = direction === 'center' ? 'circle at center' :
                                                   direction === 'top-left' ? 'circle at top left' :
                                                   direction === 'top-right' ? 'circle at top right' :
                                                   direction === 'bottom-left' ? 'circle at bottom left' :
                                                   direction === 'bottom-right' ? 'circle at bottom right' :
                                                   'circle at center';
                                    gradient = `radial-gradient(${position}, ${color1}, ${color2})`;
                                  } else {
                                    gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                  }
                                  
                                  console.log('🚀 Auto-applying gradient from hex input:', gradient);
                                  updatedElement.props.backgroundImage = gradient;
                                }
                                
                                // Para contenedores, también actualizar la propiedad responsiva correspondiente al viewport actual
                                if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                                  updatedElement.props.responsive = {
                                    ...selectedElement.props.responsive,
                                    [viewportMode]: {
                                      ...selectedElement.props.responsive[viewportMode],
                                      gradientColor1: value,
                                      ...(updatedElement.props.backgroundImage && { backgroundImage: updatedElement.props.backgroundImage })
                                    }
                                  };
                                }
                                
                                // Para contenedores, preservar explícitamente los children
                                if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                                  updatedElement.props.children = selectedElement.props.children;
                                }
                                
                                console.log('✅ Calling onUpdateElement with final element:', updatedElement);
                                onUpdateElement(updatedElement);
                              }}
                              className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d]"
                              placeholder="#ff1b6d"
                              maxLength={7}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs text-gray-400 mb-2">Color final</label>
                          <div className="flex items-center gap-3">
                            <div className="relative w-8 h-8 flex-shrink-0">
                              <div 
                                className="w-full h-full rounded-full border-2 border-[#3a3a3a] overflow-hidden relative"
                                style={{
                                  backgroundColor: selectedElement.props.gradientColor2 || '#8b5cf6'
                                }}
                              >
                                <input
                                  type="color"
                                  value={selectedElement.props.gradientColor2 || '#8b5cf6'}
                                  onChange={(e) => {
                                    console.log('🎨 Gradient color 2 changed to:', e.target.value);
                                    handlePropertyChange('gradientColor2', e.target.value);
                                    // Auto-aplicar degradado al cambiar color
                                    setTimeout(() => {
                                      const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                                      const color2 = e.target.value;
                                      const type = selectedElement.props.gradientType || 'linear';
                                      const direction = selectedElement.props.gradientDirection || '135deg';
                                      
                                      let gradient;
                                      if (type === 'radial') {
                                        const position = direction === 'center' ? 'circle at center' :
                                                       direction === 'top-left' ? 'circle at top left' :
                                                       direction === 'top-right' ? 'circle at top right' :
                                                       direction === 'bottom-left' ? 'circle at bottom left' :
                                                       direction === 'bottom-right' ? 'circle at bottom right' :
                                                       'circle at center';
                                        gradient = `radial-gradient(${position}, ${color1}, ${color2})`;
                                      } else {
                                        gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                      }
                                      
                                      console.log('🚀 Auto-applying gradient:', gradient);
                                      handlePropertyChange('backgroundImage', gradient);
                                    }, 10);
                                  }}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                                  style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent'
                                  }}
                                  title="Selecciona el color final"
                                />
                              </div>
                            </div>
                            <input
                              type="text"
                              value={selectedElement.props.gradientColor2 || '#8b5cf6'}
                              onChange={(e) => {
                                const value = e.target.value;
                                console.log('✏️ Gradient color 2 hex changed to:', value);
                                
                                // Crear el elemento actualizado con el nuevo color
                                const updatedElement = {
                                  ...selectedElement,
                                  props: {
                                    ...selectedElement.props,
                                    gradientColor2: value,
                                  },
                                };
                                
                                // Auto-aplicar degradado solo si es un color válido
                                if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                  const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                                  const color2 = value;
                                  const type = selectedElement.props.gradientType || 'linear';
                                  const direction = selectedElement.props.gradientDirection || '135deg';
                                  
                                  let gradient;
                                  if (type === 'radial') {
                                    const position = direction === 'center' ? 'circle at center' :
                                                   direction === 'top-left' ? 'circle at top left' :
                                                   direction === 'top-right' ? 'circle at top right' :
                                                   direction === 'bottom-left' ? 'circle at bottom left' :
                                                   direction === 'bottom-right' ? 'circle at bottom right' :
                                                   'circle at center';
                                    gradient = `radial-gradient(${position}, ${color1}, ${color2})`;
                                  } else {
                                    gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                  }
                                  
                                  console.log('🚀 Auto-applying gradient from hex input:', gradient);
                                  updatedElement.props.backgroundImage = gradient;
                                }
                                
                                // Para contenedores, también actualizar la propiedad responsiva correspondiente al viewport actual
                                if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                                  updatedElement.props.responsive = {
                                    ...selectedElement.props.responsive,
                                    [viewportMode]: {
                                      ...selectedElement.props.responsive[viewportMode],
                                      gradientColor2: value,
                                      ...(updatedElement.props.backgroundImage && { backgroundImage: updatedElement.props.backgroundImage })
                                    }
                                  };
                                }
                                
                                // Para contenedores, preservar explícitamente los children
                                if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                                  updatedElement.props.children = selectedElement.props.children;
                                }
                                
                                console.log('✅ Calling onUpdateElement with final element:', updatedElement);
                                onUpdateElement(updatedElement);
                              }}
                              className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d]"
                              placeholder="#8b5cf6"
                              maxLength={7}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Selector de tipo de degradado */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-2">Tipo de degradado</label>
                        <div className="flex gap-2">
                          {[
                            { value: 'linear', label: 'Lineal', icon: <FiArrowUpRight className="w-5 h-5" /> },
                            { value: 'radial', label: 'Radial', icon: <FiCircle className="w-5 h-5" /> }
                          ].map((type) => (
                            <button
                              key={type.value}
                              onClick={() => {
                                console.log('🔄 Gradient type changed to:', type.value);
                                handlePropertyChange('gradientType', type.value);
                                // Auto-aplicar degradado con nuevo tipo
                                setTimeout(() => {
                                  const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                                  const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                                  const direction = selectedElement.props.gradientDirection || '135deg';
                                  
                                  let gradient;
                                  if (type.value === 'radial') {
                                    const position = direction === 'center' ? 'circle at center' :
                                                   direction === 'top-left' ? 'circle at top left' :
                                                   direction === 'top-right' ? 'circle at top right' :
                                                   direction === 'bottom-left' ? 'circle at bottom left' :
                                                   direction === 'bottom-right' ? 'circle at bottom right' :
                                                   'circle at center';
                                    gradient = `radial-gradient(${position}, ${color1}, ${color2})`;
                                  } else {
                                    gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                  }
                                  
                                  console.log('🚀 Auto-applying gradient with new type:', gradient);
                                  handlePropertyChange('backgroundImage', gradient);
                                }, 10);
                              }}
                              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                                selectedElement.props.gradientType === type.value
                                  ? 'bg-[#ff1b6d] text-white shadow-md'
                                  : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a] hover:text-white'
                              }`}
                            >
                              {type.icon}
                              {type.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Selector de dirección del degradado */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-2">
                          {selectedElement.props.gradientType === 'radial' ? 'Posición del centro' : 'Dirección del degradado'}
                        </label>
                        
                        {selectedElement.props.gradientType === 'radial' ? (
                          /* Opciones para degradado radial */
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { value: 'center', label: 'Centro', icon: <FiCircle className="w-5 h-5" /> },
                              { value: 'top-left', label: 'Sup. Izq.', icon: <FiCornerLeftUp className="w-5 h-5" /> },
                              { value: 'top-right', label: 'Sup. Der.', icon: <FiCornerRightUp className="w-5 h-5" /> },
                              { value: 'bottom-left', label: 'Inf. Izq.', icon: <FiCornerLeftDown className="w-5 h-5" /> },
                              { value: 'bottom-right', label: 'Inf. Der.', icon: <FiCornerRightDown className="w-5 h-5" /> }
                            ].map((position) => (
                              <button
                                key={position.value}
                                onClick={() => {
                                  console.log('🔄 Radial gradient position changed to:', position.value);
                                  handlePropertyChange('gradientDirection', position.value);
                                  // Auto-aplicar degradado con nueva posición
                                  setTimeout(() => {
                                    const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                                    const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                                    
                                    const positionStr = position.value === 'center' ? 'circle at center' :
                                                       position.value === 'top-left' ? 'circle at top left' :
                                                       position.value === 'top-right' ? 'circle at top right' :
                                                       position.value === 'bottom-left' ? 'circle at bottom left' :
                                                       position.value === 'bottom-right' ? 'circle at bottom right' :
                                                       'circle at center';
                                    
                                    const gradient = `radial-gradient(${positionStr}, ${color1}, ${color2})`;
                                    console.log('🚀 Auto-applying radial gradient:', gradient);
                                    handlePropertyChange('backgroundImage', gradient);
                                  }, 10);
                                }}
                                className={`flex items-center justify-center px-2 py-3 rounded-lg text-xs transition-all ${
                                  (selectedElement.props.gradientDirection || 'center') === position.value
                                    ? 'bg-[#ff1b6d] text-white shadow-md'
                                    : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a] hover:text-white'
                                }`}
                                title={position.label}
                              >
                                {position.icon}
                              </button>
                            ))}
                          </div>
                        ) : (
                          /* Opciones para degradado lineal */
                          <div className="grid grid-cols-4 gap-2">
                            {[
                              { value: '0deg', label: 'Abajo', icon: <FiArrowDown className="w-5 h-5" /> },
                              { value: '45deg', label: 'Abajo Der.', icon: <FiCornerRightDown className="w-5 h-5" /> },
                              { value: '90deg', label: 'Derecha', icon: <FiArrowRight className="w-5 h-5" /> },
                              { value: '135deg', label: 'Arriba Der.', icon: <FiCornerRightUp className="w-5 h-5" /> },
                              { value: '180deg', label: 'Arriba', icon: <FiArrowUp className="w-5 h-5" /> },
                              { value: '225deg', label: 'Arriba Izq.', icon: <FiCornerLeftUp className="w-5 h-5" /> },
                              { value: '270deg', label: 'Izquierda', icon: <FiArrowLeft className="w-5 h-5" /> },
                              { value: '315deg', label: 'Abajo Izq.', icon: <FiCornerLeftDown className="w-5 h-5" /> }
                            ].map((direction) => (
                              <button
                                key={direction.value}
                                onClick={() => {
                                  console.log('🔄 Linear gradient direction changed to:', direction.value);
                                  handlePropertyChange('gradientDirection', direction.value);
                                  // Auto-aplicar degradado con nueva dirección
                                  setTimeout(() => {
                                    const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                                    const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                                    const gradient = `linear-gradient(${direction.value}, ${color1}, ${color2})`;
                                    console.log('🚀 Auto-applying linear gradient:', gradient);
                                    handlePropertyChange('backgroundImage', gradient);
                                  }, 10);
                                }}
                                className={`flex items-center justify-center px-2 py-2 rounded-lg text-xs transition-all ${
                                  (selectedElement.props.gradientDirection || '135deg') === direction.value
                                    ? 'bg-[#ff1b6d] text-white shadow-md'
                                    : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a] hover:text-white'
                                }`}
                                title={direction.label}
                              >
                                {direction.icon}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Botón para eliminar degradado */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            console.log('🗑️ Removing gradient');
                            handlePropertyChange('backgroundImage', 'none');
                            handlePropertyChange('backgroundColor', 'transparent');
                          }}
                          className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <FiTrash2 className="w-4 h-4" />
                          Eliminar Degradado
                        </button>
                        
                        <button
                          onClick={() => {
                            // Intercambiar colores
                            const color1 = selectedElement.props.gradientColor1 || '#ff1b6d';
                            const color2 = selectedElement.props.gradientColor2 || '#8b5cf6';
                            
                            console.log('🔄 Swapping gradient colors');
                            handlePropertyChange('gradientColor1', color2);
                            handlePropertyChange('gradientColor2', color1);
                            
                            // Auto-aplicar degradado con colores intercambiados
                            setTimeout(() => {
                              const type = selectedElement.props.gradientType || 'linear';
                              const direction = selectedElement.props.gradientDirection || '135deg';
                              
                              let gradient;
                              if (type === 'radial') {
                                const position = direction === 'center' ? 'circle at center' :
                                               direction === 'top-left' ? 'circle at top left' :
                                               direction === 'top-right' ? 'circle at top right' :
                                               direction === 'bottom-left' ? 'circle at bottom left' :
                                               direction === 'bottom-right' ? 'circle at bottom right' :
                                               'circle at center';
                                gradient = `radial-gradient(${position}, ${color2}, ${color1})`;
                              } else {
                                gradient = `linear-gradient(${direction}, ${color2}, ${color1})`;
                              }
                              
                              console.log('🚀 Auto-applying swapped gradient:', gradient);
                              handlePropertyChange('backgroundImage', gradient);
                            }, 10);
                          }}
                          className="px-3 py-2 bg-[#3a3a3a] text-white rounded-lg hover:bg-[#4a4a4a] transition-colors text-sm flex items-center justify-center"
                          title="Intercambiar colores"
                        >
                          <FiRotateCw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {currentBackgroundMode === 'glass' && (
                    <div className="p-6 bg-gradient-to-br from-slate-800/30 to-purple-900/35 border border-purple-500/40 rounded-xl space-y-6">
                      {/* Color del cristal */}
                      <div>
                        <label className="block text-xs text-purple-300 font-medium mb-3">Color del cristal</label>
                        <div className="flex items-center gap-3">
                          <div className="relative w-8 h-8 flex-shrink-0">
                            <div 
                              className="w-full h-full rounded-full border-2 border-purple-400/50 overflow-hidden relative"
                              style={{
                                backgroundColor: selectedElement.props.glassColor || '#ffffff'
                              }}
                            >
                              <input
                                type="color"
                                value={selectedElement.props.glassColor || '#ffffff'}
                                onChange={(e) => handlePropertyChange('glassColor', e.target.value)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent'
                                }}
                                title="Selecciona el color del cristal"
                              />
                            </div>
                          </div>
                          <input
                            type="text"
                            value={selectedElement.props.glassColor || '#ffffff'}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Permitir escribir mientras se tipea, validar solo en blur
                              handlePropertyChange('glassColor', value);
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              // Validar al perder el foco y corregir si es necesario
                              if (value === '' || !value.startsWith('#')) {
                                handlePropertyChange('glassColor', '#ffffff');
                              } else if (!/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                // Si no es válido, mantener solo los caracteres válidos
                                const cleanValue = value.replace(/[^#0-9A-Fa-f]/g, '').slice(0, 7);
                                if (cleanValue.length === 7) {
                                  handlePropertyChange('glassColor', cleanValue);
                                } else {
                                  handlePropertyChange('glassColor', '#ffffff');
                                }
                              }
                            }}
                            className="w-24 px-2 py-2 bg-[#2a2a2a] border border-purple-400/50 rounded text-white text-sm font-mono focus:border-purple-400 focus:ring-2 focus:ring-purple-400/30"
                            placeholder="#ffffff"
                            maxLength={7}
                          />
                        </div>
                      </div>
                      
                      {/* Opacidad del fondo */}
                      <div>
                        <label className="block text-xs text-purple-300 font-medium mb-3">Opacidad del fondo</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={(selectedElement.props.glassOpacity || 20)}
                            onChange={(e) => handlePropertyChange('glassOpacity', parseInt(e.target.value))}
                            style={{
                              '--progress': `${(selectedElement.props.glassOpacity || 20)}%`
                            }}
                            className="flex-1 slider-custom"
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={selectedElement.props.glassOpacity || 20}
                            onChange={(e) => handlePropertyChange('glassOpacity', parseInt(e.target.value) || 0)}
                            className="w-16 px-2 py-1 bg-[#2a2a2a] border border-purple-400/50 rounded text-white text-sm text-center focus:border-purple-400 focus:ring-1 focus:ring-purple-400/30"
                            title="Opacidad en porcentaje"
                          />
                          <span className="text-xs text-purple-300 whitespace-nowrap min-w-[20px] font-medium">%</span>
                        </div>
                      </div>
                      
                      {/* Intensidad del blur */}
                      <div>
                        <label className="block text-xs text-purple-300 font-medium mb-3">Intensidad del blur</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min="0"
                            max="50"
                            value={(selectedElement.props.glassBlur || 10)}
                            onChange={(e) => handlePropertyChange('glassBlur', parseInt(e.target.value))}
                            style={{
                              '--progress': `${((selectedElement.props.glassBlur || 10) / 50) * 100}%`
                            }}
                            className="flex-1 slider-custom"
                          />
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={selectedElement.props.glassBlur || 10}
                            onChange={(e) => handlePropertyChange('glassBlur', parseInt(e.target.value) || 0)}
                            className="w-16 px-2 py-1 bg-[#2a2a2a] border border-purple-400/50 rounded text-white text-sm text-center focus:border-purple-400 focus:ring-1 focus:ring-purple-400/30"
                            title="Blur en píxeles"
                          />
                          <span className="text-xs text-purple-300 whitespace-nowrap min-w-[20px] font-medium">px</span>
                        </div>
                      </div>
                      
                      {/* Botón para eliminar efecto glass */}
                      <div className="pt-2">
                        <button
                          onClick={() => {
                            console.log('🗑️ Removing glass effect');
                            handlePropertyChange('glassEffect', false);
                            handlePropertyChange('backgroundColor', 'transparent');
                          }}
                          className="w-full px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <FiTrash2 className="w-4 h-4" />
                          Eliminar Efecto Glass
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Acordeón de Borde */}
            <div>
              <button
                onClick={() => setStyleAccordions(prev => ({ ...prev, border: !prev.border }))}
                className="w-full flex items-center justify-between py-2 hover:bg-[#2a2a2a] rounded transition-colors"
              >
                <span className="text-sm font-medium text-gray-300">
                  Borde
                </span>
                <FiChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                  styleAccordions.border ? 'rotate-90' : ''
                }`} />
              </button>
              
              {styleAccordions.border && (
                <div className="mt-4 space-y-6">
                  {/* Radio del borde con slider */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Radio del borde</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="0"
                        max="50"
                        value={parseInt((selectedElement.props.borderRadius || '0px').replace('px', '')) || 0}
                        onChange={(e) => handlePropertyChange('borderRadius', `${e.target.value}px`)}
                        style={{
                          '--progress': `${((parseInt((selectedElement.props.borderRadius || '0px').replace('px', '')) || 0) / 50) * 100}%`
                        }}
                        className="flex-1 slider-custom"
                      />
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={parseInt((selectedElement.props.borderRadius || '0px').replace('px', '')) || 0}
                        onChange={(e) => handlePropertyChange('borderRadius', `${e.target.value}px`)}
                        className="w-20 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm"
                        title="Radio en píxeles"
                      />
                      <span className="text-xs text-gray-400 whitespace-nowrap min-w-[40px]">px</span>
                    </div>
                  </div>

                  {/* Tipo de borde */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Tipo de borde</label>
                    <select
                      value={(() => {
                        const border = selectedElement.props.border || 'none';
                        const borderStyle = selectedElement.props.borderStyle || 'none';
                        console.log('🔍 Current border state:', { border, borderStyle });
                        
                        if (border === 'none' || borderStyle === 'none') return 'none';
                        if (border.includes('solid') || borderStyle === 'solid') return 'solid';
                        if (border.includes('dotted') || borderStyle === 'dotted') return 'dotted';
                        if (border.includes('dashed') || borderStyle === 'dashed') return 'dashed';
                        return 'none';
                      })()}
                      onChange={(e) => {
                        const borderType = e.target.value;
                        console.log('🔧 Border type changed to:', borderType);
                        console.log('🔍 Current element props before change:', selectedElement.props);
                        
                        // Crear el objeto de actualización completo
                        let updates = {};
                        
                        if (borderType === 'none') {
                          console.log('❌ Setting border to none');
                          updates = {
                            border: 'none',
                            borderStyle: 'none',
                            borderWidth: '0',
                            borderColor: '#000000',
                            borderImage: 'none',
                            borderColorMode: 'solid'
                          };
                        } else {
                          console.log('✅ Setting border to:', borderType);
                          
                          // Usar valores existentes o por defecto
                          const currentWidth = selectedElement.props.borderWidth || '1';
                          const currentColor = selectedElement.props.borderColor || '#000000';
                          const borderString = `${currentWidth}px ${borderType} ${currentColor}`;
                          
                          console.log('📝 Applying border with:', {
                            width: currentWidth,
                            style: borderType,
                            color: currentColor,
                            borderString
                          });
                          
                          updates = {
                            border: borderString,
                            borderStyle: borderType,
                            borderWidth: currentWidth,
                            borderColor: currentColor,
                            borderImage: 'none', // Reset border image when using solid color
                            borderColorMode: 'solid' // Default to solid color mode
                          };
                        }
                        
                        console.log('🚀 All border updates to apply:', updates);
                        
                        // Aplicar todas las actualizaciones de una vez
                        const updatedElement = {
                          ...selectedElement,
                          props: {
                            ...selectedElement.props,
                            ...updates
                          },
                        };
                        
                        // Para contenedores, también actualizar la propiedad responsiva
                        if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                          updatedElement.props.responsive = {
                            ...selectedElement.props.responsive,
                            [viewportMode]: {
                              ...selectedElement.props.responsive[viewportMode],
                              ...updates
                            }
                          };
                        }
                        
                        // Preservar children para contenedores
                        if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                          updatedElement.props.children = selectedElement.props.children;
                        }
                        
                        console.log('✅ Final updated element:', updatedElement);
                        onUpdateElement(updatedElement);
                      }}
                      className="w-full px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    >
                      <option value="none">Ninguno</option>
                      <option value="solid">Continuo</option>
                      <option value="dotted">Punteado</option>
                      <option value="dashed">Discontinuo</option>
                    </select>
                  </div>

                  {/* Grosor del borde - solo visible cuando no es 'none' */}
                  {(() => {
                    const border = selectedElement.props.border || 'none';
                    return border !== 'none' && !border.includes('none');
                  })() && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-3">Grosor del borde</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={parseInt(selectedElement.props.borderWidth || '1')}
                          onChange={(e) => {
                            const newWidth = e.target.value;
                            console.log('🎚️ Border width slider changed to:', newWidth);
                            
                            // Obtener valores actuales
                            const currentStyle = selectedElement.props.borderStyle || 'solid';
                            const currentColor = selectedElement.props.borderColor || '#000000';
                            const currentColorMode = selectedElement.props.borderColorMode || 'solid';
                            
                            console.log('📋 Current border properties:', {
                              width: selectedElement.props.borderWidth,
                              style: currentStyle,
                              color: currentColor,
                              colorMode: currentColorMode
                            });
                            
                            // Crear objeto de actualización completo
                            let updates = {
                              borderWidth: newWidth
                            };
                            
                            if (currentColorMode === 'gradient') {
                              // Si está en modo degradado, actualizar border-image con el nuevo grosor
                              const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                              const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                              const direction = selectedElement.props.borderGradientDirection || '0deg';
                              const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                              
                              updates.borderImage = `${gradient} 1`;
                              updates.border = `${newWidth}px solid transparent`;
                            } else {
                              // Si está en modo sólido, actualizar border normal
                              updates.border = `${newWidth}px ${currentStyle} ${currentColor}`;
                            }
                            
                            console.log('🚀 Applying border width updates:', updates);
                            
                            // Aplicar todas las actualizaciones de una vez
                            const updatedElement = {
                              ...selectedElement,
                              props: {
                                ...selectedElement.props,
                                ...updates
                              },
                            };
                            
                            // Para contenedores, también actualizar la propiedad responsiva
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                              updatedElement.props.responsive = {
                                ...selectedElement.props.responsive,
                                [viewportMode]: {
                                  ...selectedElement.props.responsive[viewportMode],
                                  ...updates
                                }
                              };
                            }
                            
                            // Preservar children para contenedores
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                              updatedElement.props.children = selectedElement.props.children;
                            }
                            
                            console.log('✅ Final updated element for width:', updatedElement);
                            onUpdateElement(updatedElement);
                          }}
                          style={{
                            '--progress': `${((parseInt(selectedElement.props.borderWidth || '1') - 1) / (10 - 1)) * 100}%`
                          }}
                          className="flex-1 slider-custom"
                        />
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={parseInt(selectedElement.props.borderWidth || '1')}
                          onChange={(e) => {
                            const newWidth = e.target.value;
                            console.log('🔢 Border width input changed to:', newWidth);
                            
                            // Obtener valores actuales
                            const currentStyle = selectedElement.props.borderStyle || 'solid';
                            const currentColor = selectedElement.props.borderColor || '#000000';
                            const currentColorMode = selectedElement.props.borderColorMode || 'solid';
                            
                            // Crear objeto de actualización completo
                            let updates = {
                              borderWidth: newWidth
                            };
                            
                            if (currentColorMode === 'gradient') {
                              // Si está en modo degradado, actualizar border-image con el nuevo grosor
                              const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                              const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                              const direction = selectedElement.props.borderGradientDirection || '0deg';
                              const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                              
                              updates.borderImage = `${gradient} 1`;
                              updates.border = `${newWidth}px solid transparent`;
                            } else {
                              // Si está en modo sólido, actualizar border normal
                              updates.border = `${newWidth}px ${currentStyle} ${currentColor}`;
                            }
                            
                            console.log('🚀 Applying border width updates:', updates);
                            
                            // Aplicar todas las actualizaciones de una vez
                            const updatedElement = {
                              ...selectedElement,
                              props: {
                                ...selectedElement.props,
                                ...updates
                              },
                            };
                            
                            // Para contenedores, también actualizar la propiedad responsiva
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                              updatedElement.props.responsive = {
                                ...selectedElement.props.responsive,
                                [viewportMode]: {
                                  ...selectedElement.props.responsive[viewportMode],
                                  ...updates
                                }
                              };
                            }
                            
                            // Preservar children para contenedores
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                              updatedElement.props.children = selectedElement.props.children;
                            }
                            
                            console.log('✅ Final updated element for width:', updatedElement);
                            onUpdateElement(updatedElement);
                          }}
                          className="w-20 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm"
                          title="Grosor en píxeles"
                        />
                        <span className="text-xs text-gray-400 whitespace-nowrap min-w-[40px]">px</span>
                      </div>
                    </div>
                  )}

                  {/* Color del borde - solo visible cuando no es 'none' */}
                  {(() => {
                    const border = selectedElement.props.border || 'none';
                    return border !== 'none' && !border.includes('none');
                  })() && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-3">Color del borde</label>
                      
                      {/* Pestañas para tipo de color del borde */}
                      <div className="flex rounded-lg overflow-hidden border border-[#3a3a3a] mb-4">
                        {[
                          { mode: 'solid', icon: <FiDroplet className="w-4 h-4" />, title: 'Color sólido' },
                          { mode: 'gradient', icon: <FiFilter className="w-4 h-4" />, title: 'Degradado' }
                        ].map((colorType) => {
                          const currentBorderColorMode = selectedElement.props.borderColorMode || 'solid';
                          
                          return (
                            <button
                              key={colorType.mode}
                              onClick={() => {
                                console.log('🎨 Border color mode clicked:', colorType.mode);
                                
                                // Crear objeto de actualización completo
                                let updates = {
                                  borderColorMode: colorType.mode
                                };
                                
                                if (colorType.mode === 'solid') {
                                  // Cambiar a color sólido
                                  const borderColor = selectedElement.props.borderColor || '#000000';
                                  const borderWidth = selectedElement.props.borderWidth || '1';
                                  const borderStyle = selectedElement.props.borderStyle || 'solid';
                                  
                                  updates.border = `${borderWidth}px ${borderStyle} ${borderColor}`;
                                  updates.borderImage = 'none';
                                } else if (colorType.mode === 'gradient') {
                                  // Cambiar a degradado - SIEMPRE establecer valores por defecto
                                  const borderWidth = selectedElement.props.borderWidth || '1'; // Por defecto 1px
                                  const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                                  const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                                  const direction = selectedElement.props.borderGradientDirection || '0deg';
                                  
                                  // SIEMPRE establecer todas las propiedades del degradado
                                  updates.borderWidth = borderWidth;
                                  updates.borderGradientColor1 = color1;
                                  updates.borderGradientColor2 = color2;
                                  updates.borderGradientDirection = direction;
                                  updates.borderStyle = 'solid';
                                  
                                  // Aplicar border-image con degradado
                                  const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                  updates.borderImage = `${gradient} 1`;
                                  updates.border = `${borderWidth}px solid transparent`;
                                  
                                  console.log('🚀 Applying gradient border updates:', updates);
                                }
                                
                                // Aplicar todas las actualizaciones de una vez
                                const updatedElement = {
                                  ...selectedElement,
                                  props: {
                                    ...selectedElement.props,
                                    ...updates
                                  },
                                };
                                
                                // Para contenedores, también actualizar la propiedad responsiva
                                if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                                  updatedElement.props.responsive = {
                                    ...selectedElement.props.responsive,
                                    [viewportMode]: {
                                      ...selectedElement.props.responsive[viewportMode],
                                      ...updates
                                    }
                                  };
                                }
                                
                                // Preservar children para contenedores
                                if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                                  updatedElement.props.children = selectedElement.props.children;
                                }
                                
                                console.log('✅ Final border color mode element:', updatedElement);
                                onUpdateElement(updatedElement);
                              }}
                              className={`flex-1 p-3 border-r border-[#3a3a3a] last:border-r-0 transition-colors ${
                                currentBorderColorMode === colorType.mode
                                  ? 'bg-[#404040] text-gray-100'
                                  : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                              }`}
                              title={colorType.title}
                            >
                              {colorType.icon}
                            </button>
                          );
                        })}
                      </div>

                      {/* Color sólido */}
                      {(selectedElement.props.borderColorMode || 'solid') === 'solid' && (
                        <div className="flex items-center gap-3">
                          <div className="relative w-8 h-8 flex-shrink-0">
                            <div 
                              className="w-full h-full rounded-full border-2 border-[#3a3a3a] overflow-hidden relative"
                              style={{
                                backgroundColor: selectedElement.props.borderColor || '#000000'
                              }}
                            >
                              <input
                                type="color"
                                value={selectedElement.props.borderColor || '#000000'}
                                onChange={(e) => {
                                  const newColor = e.target.value;
                                  const borderWidth = selectedElement.props.borderWidth || '1';
                                  const borderStyle = selectedElement.props.borderStyle || 'solid';
                                  
                                  handlePropertyChange('borderColor', newColor);
                                  handlePropertyChange('border', `${borderWidth}px ${borderStyle} ${newColor}`);
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent'
                                }}
                                title="Selecciona el color del borde"
                              />
                            </div>
                          </div>
                          <input
                            type="text"
                            value={selectedElement.props.borderColor || '#000000'}
                            onChange={(e) => {
                              const value = e.target.value;
                              handlePropertyChange('borderColor', value);
                              
                              // Auto-aplicar borde si es un color válido
                              if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                const borderWidth = selectedElement.props.borderWidth || '1';
                                const borderStyle = selectedElement.props.borderStyle || 'solid';
                                handlePropertyChange('border', `${borderWidth}px ${borderStyle} ${value}`);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value === '' || !value.startsWith('#')) {
                                handlePropertyChange('borderColor', '#000000');
                              } else if (!/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                const cleanValue = value.replace(/[^#0-9A-Fa-f]/g, '').slice(0, 7);
                                if (cleanValue.length === 7) {
                                  handlePropertyChange('borderColor', cleanValue);
                                } else {
                                  handlePropertyChange('borderColor', '#000000');
                                }
                              }
                            }}
                            className="w-24 px-2 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            placeholder="#000000"
                            maxLength={7}
                          />
                        </div>
                      )}

                      {/* Degradado para borde */}
                      {selectedElement.props.borderColorMode === 'gradient' && (
                        <div className="space-y-4">
                          {/* Vista previa del degradado del borde */}
                          <div className="relative">
                            <div 
                              className="w-full h-16 rounded-lg border-4 transition-all duration-300"
                              style={{
                                borderImage: (() => {
                                  const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                                  const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                                  const direction = selectedElement.props.borderGradientDirection || '0deg';
                                  return `linear-gradient(${direction}, ${color1}, ${color2}) 1`;
                                })(),
                                borderStyle: 'solid',
                                borderWidth: `${selectedElement.props.borderWidth || '1'}px`,
                                backgroundColor: '#f3f4f6'
                              }}
                            ></div>
                            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                              Borde con Degradado
                            </div>
                          </div>
                          
                          {/* Selectores de color para degradado del borde */}
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs text-gray-400 mb-2">Color inicial</label>
                              <div className="flex items-center gap-3">
                                <div className="relative w-8 h-8 flex-shrink-0">
                                  <div 
                                    className="w-full h-full rounded-full border-2 border-[#3a3a3a] overflow-hidden relative"
                                    style={{
                                      backgroundColor: selectedElement.props.borderGradientColor1 || '#ff1b6d'
                                    }}
                                  >
                                    <input
                                      type="color"
                                      value={selectedElement.props.borderGradientColor1 || '#ff1b6d'}
                                      onChange={(e) => {
                                        const newColor = e.target.value;
                                        handlePropertyChange('borderGradientColor1', newColor);
                                        
                                        // Auto-aplicar degradado del borde
                                        setTimeout(() => {
                                          const color1 = newColor;
                                          const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                                          const direction = selectedElement.props.borderGradientDirection || '0deg';
                                          const borderWidth = selectedElement.props.borderWidth || '1';
                                          
                                          const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                          handlePropertyChange('borderImage', `${gradient} 1`);
                                          handlePropertyChange('border', `${borderWidth}px solid transparent`);
                                        }, 10);
                                      }}
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                                      style={{
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent'
                                      }}
                                      title="Selecciona el color inicial del borde"
                                    />
                                  </div>
                                </div>
                                <input
                                  type="text"
                                  value={selectedElement.props.borderGradientColor1 || '#ff1b6d'}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    handlePropertyChange('borderGradientColor1', value);
                                    
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                      const color1 = value;
                                      const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                                      const direction = selectedElement.props.borderGradientDirection || '0deg';
                                      const borderWidth = selectedElement.props.borderWidth || '1';
                                      
                                      const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                      handlePropertyChange('borderImage', `${gradient} 1`);
                                      handlePropertyChange('border', `${borderWidth}px solid transparent`);
                                    }
                                  }}
                                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d]"
                                  placeholder="#ff1b6d"
                                  maxLength={7}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-xs text-gray-400 mb-2">Color final</label>
                              <div className="flex items-center gap-3">
                                <div className="relative w-8 h-8 flex-shrink-0">
                                  <div 
                                    className="w-full h-full rounded-full border-2 border-[#3a3a3a] overflow-hidden relative"
                                    style={{
                                      backgroundColor: selectedElement.props.borderGradientColor2 || '#8b5cf6'
                                    }}
                                  >
                                    <input
                                      type="color"
                                      value={selectedElement.props.borderGradientColor2 || '#8b5cf6'}
                                      onChange={(e) => {
                                        const newColor = e.target.value;
                                        handlePropertyChange('borderGradientColor2', newColor);
                                        
                                        // Auto-aplicar degradado del borde
                                        setTimeout(() => {
                                          const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                                          const color2 = newColor;
                                          const direction = selectedElement.props.borderGradientDirection || '0deg';
                                          const borderWidth = selectedElement.props.borderWidth || '1';
                                          
                                          const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                          handlePropertyChange('borderImage', `${gradient} 1`);
                                          handlePropertyChange('border', `${borderWidth}px solid transparent`);
                                        }, 10);
                                      }}
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                                      style={{
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent'
                                      }}
                                      title="Selecciona el color final del borde"
                                    />
                                  </div>
                                </div>
                                <input
                                  type="text"
                                  value={selectedElement.props.borderGradientColor2 || '#8b5cf6'}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    handlePropertyChange('borderGradientColor2', value);
                                    
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                      const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                                      const color2 = value;
                                      const direction = selectedElement.props.borderGradientDirection || '0deg';
                                      const borderWidth = selectedElement.props.borderWidth || '1';
                                      
                                      const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
                                      handlePropertyChange('borderImage', `${gradient} 1`);
                                      handlePropertyChange('border', `${borderWidth}px solid transparent`);
                                    }
                                  }}
                                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d]"
                                  placeholder="#8b5cf6"
                                  maxLength={7}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Selector de dirección del degradado del borde */}
                          <div>
                            <label className="block text-xs text-gray-400 mb-2">Dirección del degradado</label>
                            <div className="grid grid-cols-4 gap-2">
                              {[
                                { value: '0deg', label: 'Abajo', icon: <FiArrowDown className="w-5 h-5" /> },
                                { value: '45deg', label: 'Abajo Der.', icon: <FiCornerRightDown className="w-5 h-5" /> },
                                { value: '90deg', label: 'Derecha', icon: <FiArrowRight className="w-5 h-5" /> },
                                { value: '135deg', label: 'Arriba Der.', icon: <FiCornerRightUp className="w-5 h-5" /> },
                                { value: '180deg', label: 'Arriba', icon: <FiArrowUp className="w-5 h-5" /> },
                                { value: '225deg', label: 'Arriba Izq.', icon: <FiCornerLeftUp className="w-5 h-5" /> },
                                { value: '270deg', label: 'Izquierda', icon: <FiArrowLeft className="w-5 h-5" /> },
                                { value: '315deg', label: 'Abajo Izq.', icon: <FiCornerLeftDown className="w-5 h-5" /> }
                              ].map((direction) => (
                                <button
                                  key={direction.value}
                                  onClick={() => {
                                    handlePropertyChange('borderGradientDirection', direction.value);
                                    
                                    // Auto-aplicar degradado del borde con nueva dirección
                                    setTimeout(() => {
                                      const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                                      const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                                      const borderWidth = selectedElement.props.borderWidth || '1';
                                      
                                      const gradient = `linear-gradient(${direction.value}, ${color1}, ${color2})`;
                                      handlePropertyChange('borderImage', `${gradient} 1`);
                                      handlePropertyChange('border', `${borderWidth}px solid transparent`);
                                    }, 10);
                                  }}
                                  className={`flex items-center justify-center px-2 py-2 rounded-lg text-xs transition-all ${
                                    (selectedElement.props.borderGradientDirection || '0deg') === direction.value
                                      ? 'bg-[#ff1b6d] text-white shadow-md'
                                      : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a] hover:text-white'
                                  }`}
                                  title={direction.label}
                                >
                                  {direction.icon}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Botones de acción para degradado del borde */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                handlePropertyChange('borderImage', 'none');
                                handlePropertyChange('border', 'none');
                                handlePropertyChange('borderColorMode', 'solid');
                              }}
                              className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                              <FiTrash2 className="w-4 h-4" />
                              Eliminar Borde
                            </button>
                            
                            <button
                              onClick={() => {
                                // Intercambiar colores del degradado del borde
                                const color1 = selectedElement.props.borderGradientColor1 || '#ff1b6d';
                                const color2 = selectedElement.props.borderGradientColor2 || '#8b5cf6';
                                
                                handlePropertyChange('borderGradientColor1', color2);
                                handlePropertyChange('borderGradientColor2', color1);
                                
                                // Auto-aplicar degradado con colores intercambiados
                                setTimeout(() => {
                                  const direction = selectedElement.props.borderGradientDirection || '0deg';
                                  const borderWidth = selectedElement.props.borderWidth || '1';
                                  
                                  const gradient = `linear-gradient(${direction}, ${color2}, ${color1})`;
                                  handlePropertyChange('borderImage', `${gradient} 1`);
                                  handlePropertyChange('border', `${borderWidth}px solid transparent`);
                                }, 10);
                              }}
                              className="px-3 py-2 bg-[#3a3a3a] text-white rounded-lg hover:bg-[#4a4a4a] transition-colors text-sm flex items-center justify-center"
                              title="Intercambiar colores"
                            >
                              <FiRotateCw className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botón para eliminar borde completamente */}
                  {(() => {
                    const border = selectedElement.props.border || 'none';
                    return border !== 'none' && !border.includes('none');
                  })() && (selectedElement.props.borderColorMode || 'solid') === 'solid' && (
                    <div className="pt-2">
                      <button
                        onClick={() => {
                          handlePropertyChange('border', 'none');
                          handlePropertyChange('borderColor', '#000000');
                          handlePropertyChange('borderWidth', '1');
                          handlePropertyChange('borderStyle', 'solid');
                        }}
                        className="w-full px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <FiTrash2 className="w-4 h-4" />
                        Eliminar Borde
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      case ELEMENT_TYPES.HEADING:
      case ELEMENT_TYPES.TEXT:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.props.color}
                  onChange={(e) => handlePropertyChange('color', e.target.value)}
                  className="w-12 h-10 bg-[#2a2a2a] border border-[#3a3a3a] rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedElement.props.color}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                      handlePropertyChange('color', value);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tamaño de fuente</label>
              <input
                type="range"
                min="12"
                max="72"
                value={parseInt(selectedElement.props.fontSize)}
                onChange={(e) => handlePropertyChange('fontSize', `${e.target.value}px`)}
                style={{
                  '--progress': `${((parseInt(selectedElement.props.fontSize) - 12) / (72 - 12)) * 100}%`
                }}
                className="w-full slider-custom"
              />
              <span className="text-xs text-gray-400">{selectedElement.props.fontSize}</span>
            </div>
          </div>
        );
      case ELEMENT_TYPES.BUTTON:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color de fondo</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.props.backgroundColor}
                  onChange={(e) => handlePropertyChange('backgroundColor', e.target.value)}
                  className="w-12 h-10 bg-[#2a2a2a] border border-[#3a3a3a] rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedElement.props.backgroundColor}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                      handlePropertyChange('backgroundColor', value);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono"
                  placeholder="#8b5cf6"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color de texto</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.props.textColor}
                  onChange={(e) => handlePropertyChange('textColor', e.target.value)}
                  className="w-12 h-10 bg-[#2a2a2a] border border-[#3a3a3a] rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedElement.props.textColor}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                      handlePropertyChange('textColor', value);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono"
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>
        );
      default:
        return <p className="text-gray-400 text-sm">No hay propiedades de estilo para este elemento</p>;
    }
  };

  // Función para obtener las propiedades avanzadas por tipo de elemento
  const getAdvancedProperties = () => {
    switch (selectedElement.type) {
      case ELEMENT_TYPES.CONTAINER:
        // Función para convertir valores entre unidades CSS
        const convertValue = (value, fromUnit, toUnit) => {
          const numValue = parseFloat(value.toString().replace(/[^0-9.]/g, '')) || 0;
          
          console.log('🔄 Converting padding units from', fromUnit, 'to', toUnit, 'Value:', numValue);
          
          if (fromUnit === toUnit) {
            return `${numValue}${toUnit}`;
          }
          
          let convertedValue = numValue;
          
          // Conversiones desde píxeles
          if (fromUnit === 'px') {
            if (toUnit === 'rem') convertedValue = numValue / 16;
            else if (toUnit === 'em') convertedValue = numValue / 16;
            else if (toUnit === '%') convertedValue = numValue / 4;
          }
          // Conversiones desde rem
          else if (fromUnit === 'rem') {
            if (toUnit === 'px') convertedValue = numValue * 16;
            else if (toUnit === 'em') convertedValue = numValue;
            else if (toUnit === '%') convertedValue = (numValue * 16) / 4;
          }
          // Conversiones desde em
          else if (fromUnit === 'em') {
            if (toUnit === 'px') convertedValue = numValue * 16;
            else if (toUnit === 'rem') convertedValue = numValue;
            else if (toUnit === '%') convertedValue = (numValue * 16) / 4;
          }
          // Conversiones desde porcentaje
          else if (fromUnit === '%') {
            if (toUnit === 'px') convertedValue = numValue * 4;
            else if (toUnit === 'rem') convertedValue = (numValue * 4) / 16;
            else if (toUnit === 'em') convertedValue = (numValue * 4) / 16;
          }
          
          // Validación y redondeo
          convertedValue = Math.max(0, convertedValue);
          if (toUnit === 'rem' || toUnit === 'em') {
            convertedValue = Math.round(convertedValue * 100) / 100;
          } else {
            convertedValue = Math.round(convertedValue);
          }
          
          console.log(`📏 Converted ${numValue}${fromUnit} to ${convertedValue}${toUnit}`);
          return `${convertedValue}${toUnit}`;
        };
        
        // Detectar unidad actual del padding
        const currentUnit = (() => {
          const topValue = selectedElement.props.paddingTop || selectedElement.props.padding || '0px';
          if (topValue.includes('rem')) return 'rem';
          if (topValue.includes('em')) return 'em';
          if (topValue.includes('%')) return '%';
          return 'px';
        })();
        
        return (
          <div className="space-y-6">
            {/* Sistema de Padding Avanzado con estilo de caja */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-300">Relleno</label>
                
                {/* Selector de unidades */}
                <select
                  value={currentUnit}
                  onChange={(e) => {
                    const newUnit = e.target.value;
                    console.log('🔄 Converting padding units from', currentUnit, 'to', newUnit);
                    
                    // Obtener valores actuales
                    const topValue = selectedElement.props.paddingTop || selectedElement.props.padding || '0px';
                    const rightValue = selectedElement.props.paddingRight || selectedElement.props.padding || '0px';
                    const bottomValue = selectedElement.props.paddingBottom || selectedElement.props.padding || '0px';
                    const leftValue = selectedElement.props.paddingLeft || selectedElement.props.padding || '0px';
                    
                    // Convertir todos los valores
                    const convertedTop = convertValue(topValue, currentUnit, newUnit);
                    const convertedRight = convertValue(rightValue, currentUnit, newUnit);
                    const convertedBottom = convertValue(bottomValue, currentUnit, newUnit);
                    const convertedLeft = convertValue(leftValue, currentUnit, newUnit);
                    
                    console.log('✅ All converted values:', {
                      top: convertedTop,
                      right: convertedRight,
                      bottom: convertedBottom,
                      left: convertedLeft
                    });
                    
                    // Actualizar todas las propiedades en una sola operación
                    const updatedElement = {
                      ...selectedElement,
                      props: {
                        ...selectedElement.props,
                        paddingTop: convertedTop,
                        paddingRight: convertedRight,
                        paddingBottom: convertedBottom,
                        paddingLeft: convertedLeft,
                        padding: convertedTop, // Usar top como valor de referencia
                      },
                    };
                    
                    // Para contenedores, también actualizar la propiedad responsiva correspondiente al viewport actual
                    if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                      updatedElement.props.responsive = {
                        ...selectedElement.props.responsive,
                        [viewportMode]: {
                          ...selectedElement.props.responsive[viewportMode],
                          paddingTop: convertedTop,
                          paddingRight: convertedRight,
                          paddingBottom: convertedBottom,
                          paddingLeft: convertedLeft,
                          padding: convertedTop,
                        }
                      };
                    }
                    
                    // Para contenedores, preservar explícitamente los children
                    if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                      updatedElement.props.children = selectedElement.props.children;
                    }
                    
                    onUpdateElement(updatedElement);
                  }}
                  className="px-2 py-1 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-xs focus:border-purple-500"
                >
                  <option value="px">px</option>
                  <option value="rem">rem</option>
                  <option value="em">em</option>
                  <option value="%">%</option>
                </select>
              </div>
              
              {/* Controles de padding en una sola línea */}
              <div className="space-y-3">
                {/* Fila de inputs */}
                <div className="flex items-stretch bg-[#2a2a2a] border border-[#404040] rounded overflow-hidden">
                  {/* Padding Top */}
                  <div className="flex-1 border-r border-[#404040]">
                    <input
                      type="number"
                      min="0"
                      max={currentUnit === 'px' ? "200" : currentUnit === '%' ? "50" : "12"}
                      step={currentUnit === 'rem' || currentUnit === 'em' ? "0.25" : "1"}
                      value={parseFloat((selectedElement.props.paddingTop || '0px').replace(/[^0-9.]/g, '')) || 0}
                      onChange={(e) => {
                        const newValue = `${e.target.value}${currentUnit}`;
                        console.log('🔄 Updating paddingTop to:', newValue, 'isPaddingLinked:', isPaddingLinked);
                        
                        if (isPaddingLinked) {
                          // Si está enlazado, actualizar todos los valores con el mismo valor
                          const updatedElement = {
                            ...selectedElement,
                            props: {
                              ...selectedElement.props,
                              paddingTop: newValue,
                              paddingRight: newValue,
                              paddingBottom: newValue,
                              paddingLeft: newValue,
                              padding: newValue,
                            },
                          };
                          
                          // Para contenedores, también actualizar la propiedad responsiva
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                            updatedElement.props.responsive = {
                              ...selectedElement.props.responsive,
                              [viewportMode]: {
                                ...selectedElement.props.responsive[viewportMode],
                                paddingTop: newValue,
                                paddingRight: newValue,
                                paddingBottom: newValue,
                                paddingLeft: newValue,
                                padding: newValue,
                              }
                            };
                          }
                          
                          // Preservar children para contenedores
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                            updatedElement.props.children = selectedElement.props.children;
                          }
                          
                          onUpdateElement(updatedElement);
                        } else {
                          // Si no está enlazado, actualizar solo el valor individual
                          handlePropertyChange('paddingTop', newValue);
                        }
                      }}
                      className="w-full px-2 py-2 bg-transparent border-0 text-white text-sm text-center focus:outline-none focus:ring-0"
                      placeholder="0"
                      title="Padding Top"
                    />
                  </div>
                  
                  {/* Padding Right */}
                  <div className="flex-1 border-r border-[#404040]">
                    <input
                      type="number"
                      min="0"
                      max={currentUnit === 'px' ? "200" : currentUnit === '%' ? "50" : "12"}
                      step={currentUnit === 'rem' || currentUnit === 'em' ? "0.25" : "1"}
                      value={parseFloat((selectedElement.props.paddingRight || '0px').replace(/[^0-9.]/g, '')) || 0}
                      onChange={(e) => {
                        const newValue = `${e.target.value}${currentUnit}`;
                        console.log('🔄 Updating paddingRight to:', newValue, 'isPaddingLinked:', isPaddingLinked);
                        
                        if (isPaddingLinked) {
                          // Si está enlazado, actualizar todos los valores con el mismo valor
                          const updatedElement = {
                            ...selectedElement,
                            props: {
                              ...selectedElement.props,
                              paddingTop: newValue,
                              paddingRight: newValue,
                              paddingBottom: newValue,
                              paddingLeft: newValue,
                              padding: newValue,
                            },
                          };
                          
                          // Para contenedores, también actualizar la propiedad responsiva
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                            updatedElement.props.responsive = {
                              ...selectedElement.props.responsive,
                              [viewportMode]: {
                                ...selectedElement.props.responsive[viewportMode],
                                paddingTop: newValue,
                                paddingRight: newValue,
                                paddingBottom: newValue,
                                paddingLeft: newValue,
                                padding: newValue,
                              }
                            };
                          }
                          
                          // Preservar children para contenedores
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                            updatedElement.props.children = selectedElement.props.children;
                          }
                          
                          onUpdateElement(updatedElement);
                        } else {
                          // Si no está enlazado, actualizar solo el valor individual
                          handlePropertyChange('paddingRight', newValue);
                        }
                      }}
                      className="w-full px-2 py-2 bg-transparent border-0 text-white text-sm text-center focus:outline-none focus:ring-0"
                      placeholder="0"
                      title="Padding Right"
                    />
                  </div>
                  
                  {/* Padding Bottom */}
                  <div className="flex-1 border-r border-[#404040]">
                    <input
                      type="number"
                      min="0"
                      max={currentUnit === 'px' ? "200" : currentUnit === '%' ? "50" : "12"}
                      step={currentUnit === 'rem' || currentUnit === 'em' ? "0.25" : "1"}
                      value={parseFloat((selectedElement.props.paddingBottom || '0px').replace(/[^0-9.]/g, '')) || 0}
                      onChange={(e) => {
                        const newValue = `${e.target.value}${currentUnit}`;
                        console.log('🔄 Updating paddingBottom to:', newValue, 'isPaddingLinked:', isPaddingLinked);
                        
                        if (isPaddingLinked) {
                          // Si está enlazado, actualizar todos los valores con el mismo valor
                          const updatedElement = {
                            ...selectedElement,
                            props: {
                              ...selectedElement.props,
                              paddingTop: newValue,
                              paddingRight: newValue,
                              paddingBottom: newValue,
                              paddingLeft: newValue,
                              padding: newValue,
                            },
                          };
                          
                          // Para contenedores, también actualizar la propiedad responsiva
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                            updatedElement.props.responsive = {
                              ...selectedElement.props.responsive,
                              [viewportMode]: {
                                ...selectedElement.props.responsive[viewportMode],
                                paddingTop: newValue,
                                paddingRight: newValue,
                                paddingBottom: newValue,
                                paddingLeft: newValue,
                                padding: newValue,
                              }
                            };
                          }
                          
                          // Preservar children para contenedores
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                            updatedElement.props.children = selectedElement.props.children;
                          }
                          
                          onUpdateElement(updatedElement);
                        } else {
                          // Si no está enlazado, actualizar solo el valor individual
                          handlePropertyChange('paddingBottom', newValue);
                        }
                      }}
                      className="w-full px-2 py-2 bg-transparent border-0 text-white text-sm text-center focus:outline-none focus:ring-0"
                      placeholder="0"
                      title="Padding Bottom"
                    />
                  </div>
                  
                  {/* Padding Left */}
                  <div className="flex-1 border-r border-[#404040]">
                    <input
                      type="number"
                      min="0"
                      max={currentUnit === 'px' ? "200" : currentUnit === '%' ? "50" : "12"}
                      step={currentUnit === 'rem' || currentUnit === 'em' ? "0.25" : "1"}
                      value={parseFloat((selectedElement.props.paddingLeft || '0px').replace(/[^0-9.]/g, '')) || 0}
                      onChange={(e) => {
                        const newValue = `${e.target.value}${currentUnit}`;
                        console.log('🔄 Updating paddingLeft to:', newValue, 'isPaddingLinked:', isPaddingLinked);
                        
                        if (isPaddingLinked) {
                          // Si está enlazado, actualizar todos los valores con el mismo valor
                          const updatedElement = {
                            ...selectedElement,
                            props: {
                              ...selectedElement.props,
                              paddingTop: newValue,
                              paddingRight: newValue,
                              paddingBottom: newValue,
                              paddingLeft: newValue,
                              padding: newValue,
                            },
                          };
                          
                          // Para contenedores, también actualizar la propiedad responsiva
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                            updatedElement.props.responsive = {
                              ...selectedElement.props.responsive,
                              [viewportMode]: {
                                ...selectedElement.props.responsive[viewportMode],
                                paddingTop: newValue,
                                paddingRight: newValue,
                                paddingBottom: newValue,
                                paddingLeft: newValue,
                                padding: newValue,
                              }
                            };
                          }
                          
                          // Preservar children para contenedores
                          if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                            updatedElement.props.children = selectedElement.props.children;
                          }
                          
                          onUpdateElement(updatedElement);
                        } else {
                          // Si no está enlazado, actualizar solo el valor individual
                          handlePropertyChange('paddingLeft', newValue);
                        }
                      }}
                      className="w-full px-2 py-2 bg-transparent border-0 text-white text-sm text-center focus:outline-none focus:ring-0"
                      placeholder="0"
                      title="Padding Left"
                    />
                  </div>
                  
                  {/* Botón de enlace */}
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => {
                        if (!isPaddingLinked) {
                          // Si no está enlazado, verificar si es la primera vez o no
                          const hasAnyPadding = selectedElement.props.paddingTop || 
                                              selectedElement.props.paddingRight || 
                                              selectedElement.props.paddingBottom || 
                                              selectedElement.props.paddingLeft || 
                                              selectedElement.props.padding;
                          
                          if (!hasAnyPadding || 
                              (selectedElement.props.paddingTop === '0px' && 
                               selectedElement.props.paddingRight === '0px' && 
                               selectedElement.props.paddingBottom === '0px' && 
                               selectedElement.props.paddingLeft === '0px')) {
                            // Primera vez: poner todos los valores a 0px y activar enlace
                            console.log('🔗 First click: setting all padding to 0px and linking');
                            const zeroValue = `0${currentUnit}`;
                            setIsPaddingLinked(true);
                            
                            // Actualizar todas las propiedades de padding a 0
                            const updatedElement = {
                              ...selectedElement,
                              props: {
                                ...selectedElement.props,
                                paddingTop: zeroValue,
                                paddingRight: zeroValue,
                                paddingBottom: zeroValue,
                                paddingLeft: zeroValue,
                                padding: zeroValue,
                              },
                            };
                            
                            // Para contenedores, también actualizar la propiedad responsiva
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                              updatedElement.props.responsive = {
                                ...selectedElement.props.responsive,
                                [viewportMode]: {
                                  ...selectedElement.props.responsive[viewportMode],
                                  paddingTop: zeroValue,
                                  paddingRight: zeroValue,
                                  paddingBottom: zeroValue,
                                  paddingLeft: zeroValue,
                                  padding: zeroValue,
                                }
                              };
                            }
                            
                            // Preservar children para contenedores
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                              updatedElement.props.children = selectedElement.props.children;
                            }
                            
                            onUpdateElement(updatedElement);
                          } else {
                            // Si ya tienen valores, tomar el valor del primer padding (arriba) y aplicarlo a todos
                            console.log('🔗 Linking existing values: taking Top value and applying to all');
                            const topValue = selectedElement.props.paddingTop || selectedElement.props.padding || `0${currentUnit}`;
                            setIsPaddingLinked(true);
                            
                            // Actualizar todas las propiedades de padding con el valor de arriba
                            const updatedElement = {
                              ...selectedElement,
                              props: {
                                ...selectedElement.props,
                                paddingTop: topValue,
                                paddingRight: topValue,
                                paddingBottom: topValue,
                                paddingLeft: topValue,
                                padding: topValue,
                              },
                            };
                            
                            // Para contenedores, también actualizar la propiedad responsiva
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.responsive) {
                              updatedElement.props.responsive = {
                                ...selectedElement.props.responsive,
                                [viewportMode]: {
                                  ...selectedElement.props.responsive[viewportMode],
                                  paddingTop: topValue,
                                  paddingRight: topValue,
                                  paddingBottom: topValue,
                                  paddingLeft: topValue,
                                  padding: topValue,
                                }
                              };
                            }
                            
                            // Preservar children para contenedores
                            if (selectedElement.type === ELEMENT_TYPES.CONTAINER && selectedElement.props.children) {
                              updatedElement.props.children = selectedElement.props.children;
                            }
                            
                            onUpdateElement(updatedElement);
                          }
                        } else {
                          // Ya está enlazado: desenlazar
                          console.log('🔓 Unlinking padding values');
                          setIsPaddingLinked(false);
                        }
                      }}
                      className={`px-3 py-2 bg-transparent transition-colors text-sm flex items-center justify-center h-full ${
                        isPaddingLinked 
                          ? 'text-blue-400 hover:text-blue-300' 
                          : 'text-gray-300 hover:text-white'
                      }`}
                      title={isPaddingLinked ? "Desenlazar padding (permite edición independiente)" : "Enlazar padding (primer clic: todo a 0px)"}
                    >
                      {isPaddingLinked ? <FiLink className="w-4 h-4" /> : <FiLink2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Fila de iconos direccionales */}
                <div className="flex items-center text-gray-400">
                  {/* Icono Top */}
                  <div className="flex-1 flex justify-center">
                    <FiArrowUp className="w-3 h-3" title="Top" />
                  </div>
                  
                  {/* Separador visual */}
                  <div className="w-px"></div>
                  
                  {/* Icono Right */}
                  <div className="flex-1 flex justify-center">
                    <FiArrowRight className="w-3 h-3" title="Right" />
                  </div>
                  
                  {/* Separador visual */}
                  <div className="w-px"></div>
                  
                  {/* Icono Bottom */}
                  <div className="flex-1 flex justify-center">
                    <FiArrowDown className="w-3 h-3" title="Bottom" />
                  </div>
                  
                  {/* Separador visual */}
                  <div className="w-px"></div>
                  
                  {/* Icono Left */}
                  <div className="flex-1 flex justify-center">
                    <FiArrowLeft className="w-3 h-3" title="Left" />
                  </div>
                  
                  {/* Espacio para mantener alineación con el botón de enlace arriba */}
                  <div className="w-[49px]"></div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <p className="text-gray-400 text-sm">No hay propiedades avanzadas para este elemento</p>;
    }
  };

  const renderProperties = () => {
    switch (selectedElement.type) {
      case ELEMENT_TYPES.CONTAINER:
        return null; // Manejado por pestañas
      case ELEMENT_TYPES.HEADING:
      case ELEMENT_TYPES.TEXT:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Texto</label>
              <textarea
                value={selectedElement.props.text}
                onChange={(e) => handlePropertyChange('text', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.props.color}
                  onChange={(e) => handlePropertyChange('color', e.target.value)}
                  className="w-12 h-10 bg-[#2a2a2a] border border-[#3a3a3a] rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedElement.props.color}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                      handlePropertyChange('color', value);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tamaño de fuente</label>
              <input
                type="range"
                min="12"
                max="72"
                value={parseInt(selectedElement.props.fontSize)}
                onChange={(e) => handlePropertyChange('fontSize', `${e.target.value}px`)}
                style={{
                  '--progress': `${((parseInt(selectedElement.props.fontSize) - 12) / (72 - 12)) * 100}%`
                }}
                className="w-full slider-custom"
              />
              <span className="text-xs text-gray-400">{selectedElement.props.fontSize}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Alineación</label>
              <div className="flex gap-1">
                {['left', 'center', 'right'].map((align) => (
                  <button
                    key={align}
                    onClick={() => handlePropertyChange('alignment', align)}
                    className={`flex-1 p-2 rounded ${
                      selectedElement.props.alignment === align
                        ? 'bg-[#8b5cf6] text-white'
                        : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                    }`}
                  >
                    {align === 'left' && <FiAlignLeft className="w-4 h-4 mx-auto" />}
                    {align === 'center' && <FiAlignCenter className="w-4 h-4 mx-auto" />}
                    {align === 'right' && <FiAlignRight className="w-4 h-4 mx-auto" />}
                  </button>
                ))}\n              </div>
            </div>
          </div>
        );
      case ELEMENT_TYPES.BUTTON:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Texto</label>
              <input
                type="text"
                value={selectedElement.props.text}
                onChange={(e) => handlePropertyChange('text', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color de fondo</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.props.backgroundColor}
                  onChange={(e) => handlePropertyChange('backgroundColor', e.target.value)}
                  className="w-12 h-10 bg-[#2a2a2a] border border-[#3a3a3a] rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedElement.props.backgroundColor}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                      handlePropertyChange('backgroundColor', value);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono"
                  placeholder="#8b5cf6"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color de texto</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.props.textColor}
                  onChange={(e) => handlePropertyChange('textColor', e.target.value)}
                  className="w-12 h-10 bg-[#2a2a2a] border border-[#3a3a3a] rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedElement.props.textColor}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                      handlePropertyChange('textColor', value);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded text-white text-sm font-mono"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Enlace</label>
              <input
                type="url"
                value={selectedElement.props.link}
                onChange={(e) => handlePropertyChange('link', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white"
                placeholder="https://..."
              />
            </div>
          </div>
        );
      default:
        return <p className="text-gray-400 text-sm">Propiedades no disponibles para este elemento</p>;
    }
  };

  return (
    <div className="w-80 bg-[#1a1a1a] border-l border-[#2a2a2a] overflow-y-auto h-full">
      {/* Sistema de pestañas para contenedores, botones y elementos de texto */}
      {(selectedElement.type === ELEMENT_TYPES.CONTAINER || 
        selectedElement.type === ELEMENT_TYPES.BUTTON ||
        selectedElement.type === ELEMENT_TYPES.HEADING ||
        selectedElement.type === ELEMENT_TYPES.TEXT) && (
        <>
          {/* Pestañas */}
          <div className="flex bg-[#2a2a2a] p-1">
            <button
              onClick={() => setActiveTab('layout')}
              className={`flex-1 flex flex-col items-center justify-center px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'layout'
                  ? 'bg-[#1f1f1f] text-gray-100 shadow-md'
                  : 'text-gray-300 hover:text-white hover:bg-[#3a3a3a]'
              }`}
            >
              <FiGrid className="w-4 h-4 mb-1" />
              Disposición
            </button>
            <button
              onClick={() => setActiveTab('style')}
              className={`flex-1 flex flex-col items-center justify-center px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'style'
                  ? 'bg-[#1f1f1f] text-gray-100 shadow-md'
                  : 'text-gray-300 hover:text-white hover:bg-[#3a3a3a]'
              }`}
            >
              <FiDroplet className="w-4 h-4 mb-1" />
              Estilo
            </button>
            <button
              onClick={() => setActiveTab('advanced')}
              className={`flex-1 flex flex-col items-center justify-center px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'advanced'
                  ? 'bg-[#1f1f1f] text-gray-100 shadow-md'
                  : 'text-gray-300 hover:text-white hover:bg-[#3a3a3a]'
              }`}
            >
              <FiSliders className="w-4 h-4 mb-1" />
              Avanzado
            </button>
          </div>

          {/* Contenido de las pestañas */}
          <div className="p-6 space-y-6">
            {activeTab === 'layout' && getLayoutProperties()}
            {activeTab === 'style' && getStyleProperties()}
            {activeTab === 'advanced' && getAdvancedProperties()}
          </div>
        </>
      )}
      
      {/* Para elementos que no usan pestañas, mostrar propiedades tradicionales */}
      {!(selectedElement.type === ELEMENT_TYPES.CONTAINER || 
        selectedElement.type === ELEMENT_TYPES.BUTTON ||
        selectedElement.type === ELEMENT_TYPES.HEADING ||
        selectedElement.type === ELEMENT_TYPES.TEXT) && (
        <>
          {/* Campo de texto para elementos de texto/encabezado */}
          {(selectedElement.type === ELEMENT_TYPES.HEADING || selectedElement.type === ELEMENT_TYPES.TEXT) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Texto</label>
              <textarea
                value={selectedElement.props.text}
                onChange={(e) => handlePropertyChange('text', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white resize-none"
                rows={3}
              />
            </div>
          )}
          
          {/* Campo de texto para botones */}
          {selectedElement.type === ELEMENT_TYPES.BUTTON && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Texto</label>
              <input
                type="text"
                value={selectedElement.props.text}
                onChange={(e) => handlePropertyChange('text', e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white"
              />
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Enlace</label>
                <input
                  type="url"
                  value={selectedElement.props.link}
                  onChange={(e) => handlePropertyChange('link', e.target.value)}
                  className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-3 py-2 text-white"
                  placeholder="https://..."
                />
              </div>
            </div>
          )}
          
          {renderProperties()}
        </>
      )}
    </div>
  );
}

// Estructuras predeterminadas de contenedores
const CONTAINER_STRUCTURES = {
  SINGLE: 'single',
  DOUBLE: 'double', 
  TRIPLE: 'triple'
};

// Plantillas de ejemplo para la biblioteca
const TEMPLATE_LIBRARY = [
  {
    id: 'landing-1',
    name: 'Landing Page Moderna',
    category: 'landing',
    description: 'Página de aterrizaje con diseño moderno y call-to-action prominente',
    preview: '/api/placeholder/300/200',
    tags: ['landing', 'moderno', 'cta'],
    elements: [
      {
        id: 'hero-section',
        type: ELEMENT_TYPES.CONTAINER,
        props: {
          layout: 'vertical',
          gap: '24px',
          padding: '60px 40px',
          backgroundColor: '#8b5cf6',
          borderRadius: '0px',
          border: 'none',
          minHeight: '400px',
          children: [
            {
              id: 'hero-title',
              type: ELEMENT_TYPES.HEADING,
              props: {
                text: 'Bienvenido a Nuestro Producto',
                level: 1,
                alignment: 'center',
                color: '#ffffff',
                fontSize: '48px',
              }
            },
            {
              id: 'hero-subtitle',
              type: ELEMENT_TYPES.TEXT,
              props: {
                text: 'La solución perfecta para todas tus necesidades',
                alignment: 'center',
                color: '#f3f4f6',
                fontSize: '20px',
              }
            },
            {
              id: 'hero-cta',
              type: ELEMENT_TYPES.BUTTON,
              props: {
                text: 'Comenzar Ahora',
                link: '#',
                backgroundColor: '#ffffff',
                textColor: '#8b5cf6',
                padding: '16px 32px',
                borderRadius: '8px',
              }
            }
          ]
        }
      }
    ]
  },
  {
    id: 'portfolio-1',
    name: 'Portfolio Profesional',
    category: 'portfolio',
    description: 'Plantilla elegante para mostrar tu trabajo y proyectos',
    preview: '/api/placeholder/300/200',
    tags: ['portfolio', 'profesional', 'galería'],
    elements: [
      {
        id: 'portfolio-header',
        type: ELEMENT_TYPES.CONTAINER,
        props: {
          layout: 'vertical',
          gap: '20px',
          padding: '40px',
          backgroundColor: '#f8f9fa',
          borderRadius: '0px',
          border: 'none',
          minHeight: '300px',
          children: [
            {
              id: 'portfolio-title',
              type: ELEMENT_TYPES.HEADING,
              props: {
                text: 'Mi Portfolio',
                level: 1,
                alignment: 'center',
                color: '#1f2937',
                fontSize: '36px',
              }
            },
            {
              id: 'portfolio-desc',
              type: ELEMENT_TYPES.TEXT,
              props: {
                text: 'Explora mi trabajo y proyectos más destacados',
                alignment: 'center',
                color: '#6b7280',
                fontSize: '18px',
              }
            }
          ]
        }
      }
    ]
  },
  {
    id: 'blog-1',
    name: 'Blog Minimalista',
    category: 'blog',
    description: 'Diseño limpio y enfocado en el contenido',
    preview: '/api/placeholder/300/200',
    tags: ['blog', 'minimalista', 'contenido'],
    elements: [
      {
        id: 'blog-header',
        type: ELEMENT_TYPES.CONTAINER,
        props: {
          layout: 'vertical',
          gap: '16px',
          padding: '32px',
          backgroundColor: '#ffffff',
          borderRadius: '0px',
          border: '1px solid #e5e7eb',
          minHeight: '200px',
          children: [
            {
              id: 'blog-title',
              type: ELEMENT_TYPES.HEADING,
              props: {
                text: 'Mi Blog Personal',
                level: 1,
                alignment: 'left',
                color: '#111827',
                fontSize: '32px',
              }
            },
            {
              id: 'blog-tagline',
              type: ELEMENT_TYPES.TEXT,
              props: {
                text: 'Pensamientos, ideas y experiencias compartidas',
                alignment: 'left',
                color: '#4b5563',
                fontSize: '16px',
              }
            }
          ]
        }
      }
    ]
  },
  {
    id: 'ecommerce-1',
    name: 'Tienda Online',
    category: 'ecommerce',
    description: 'Plantilla perfecta para tu tienda en línea',
    preview: '/api/placeholder/300/200',
    tags: ['ecommerce', 'tienda', 'productos'],
    elements: [
      {
        id: 'shop-hero',
        type: ELEMENT_TYPES.CONTAINER,
        props: {
          layout: 'horizontal',
          gap: '40px',
          padding: '40px',
          backgroundColor: '#f9fafb',
          borderRadius: '0px',
          border: 'none',
          minHeight: '350px',
          children: [
            {
              id: 'shop-content',
              type: ELEMENT_TYPES.CONTAINER,
              props: {
                layout: 'vertical',
                gap: '20px',
                padding: '0px',
                backgroundColor: 'transparent',
                borderRadius: '0px',
                border: 'none',
                minHeight: '200px',
                children: [
                  {
                    id: 'shop-title',
                    type: ELEMENT_TYPES.HEADING,
                    props: {
                      text: 'Descubre Nuestros Productos',
                      level: 1,
                      alignment: 'left',
                      color: '#1f2937',
                      fontSize: '40px',
                    }
                  },
                  {
                    id: 'shop-subtitle',
                    type: ELEMENT_TYPES.TEXT,
                    props: {
                      text: 'Calidad premium al mejor precio',
                      alignment: 'left',
                      color: '#6b7280',
                      fontSize: '18px',
                    }
                  },
                  {
                    id: 'shop-cta',
                    type: ELEMENT_TYPES.BUTTON,
                    props: {
                      text: 'Ver Catálogo',
                      link: '#',
                      backgroundColor: '#059669',
                      textColor: '#ffffff',
                      padding: '14px 28px',
                      borderRadius: '6px',
                    }
                  }
                ]
              }
            },
            {
              id: 'shop-image',
              type: ELEMENT_TYPES.IMAGE,
              props: {
                src: '/api/placeholder/400/300',
                alt: 'Productos destacados',
                width: '100%',
                height: '280px',
              }
            }
          ]
        }
      }
    ]
  },
  {
    id: 'corporate-1',
    name: 'Corporativo Profesional',
    category: 'corporate',
    description: 'Diseño empresarial serio y confiable',
    preview: '/api/placeholder/300/200',
    tags: ['corporativo', 'empresarial', 'profesional'],
    elements: [
      {
        id: 'corporate-header',
        type: ELEMENT_TYPES.CONTAINER,
        props: {
          layout: 'vertical',
          gap: '30px',
          padding: '50px 40px',
          backgroundColor: '#1f2937',
          borderRadius: '0px',
          border: 'none',
          minHeight: '400px',
          children: [
            {
              id: 'corporate-title',
              type: ELEMENT_TYPES.HEADING,
              props: {
                text: 'Soluciones Empresariales',
                level: 1,
                alignment: 'center',
                color: '#ffffff',
                fontSize: '42px',
              }
            },
            {
              id: 'corporate-desc',
              type: ELEMENT_TYPES.TEXT,
              props: {
                text: 'Liderando la innovación en el sector empresarial con más de 20 años de experiencia',
                alignment: 'center',
                color: '#d1d5db',
                fontSize: '18px',
              }
            },
            {
              id: 'corporate-cta',
              type: ELEMENT_TYPES.BUTTON,
              props: {
                text: 'Conoce Más',
                link: '#',
                backgroundColor: '#3b82f6',
                textColor: '#ffffff',
                padding: '16px 32px',
                borderRadius: '4px',
              }
            }
          ]
        }
      }
    ]
  },
  {
    id: 'creative-1',
    name: 'Creativo y Artístico',
    category: 'creative',
    description: 'Para artistas y creativos que quieren destacar',
    preview: '/api/placeholder/300/200',
    tags: ['creativo', 'artístico', 'colorido'],
    elements: [
      {
        id: 'creative-hero',
        type: ELEMENT_TYPES.CONTAINER,
        props: {
          layout: 'vertical',
          gap: '25px',
          padding: '45px',
          backgroundColor: '#ec4899',
          borderRadius: '0px',
          border: 'none',
          minHeight: '380px',
          children: [
            {
              id: 'creative-title',
              type: ELEMENT_TYPES.HEADING,
              props: {
                text: 'Arte y Creatividad',
                level: 1,
                alignment: 'center',
                color: '#ffffff',
                fontSize: '44px',
              }
            },
            {
              id: 'creative-subtitle',
              type: ELEMENT_TYPES.TEXT,
              props: {
                text: 'Donde la imaginación cobra vida',
                alignment: 'center',
                color: '#fce7f3',
                fontSize: '20px',
              }
            },
            {
              id: 'creative-cta',
              type: ELEMENT_TYPES.BUTTON,
              props: {
                text: 'Explorar Galería',
                link: '#',
                backgroundColor: '#ffffff',
                textColor: '#ec4899',
                padding: '16px 32px',
                borderRadius: '25px',
              }
            }
          ]
        }
      }
    ]
  }
];

// Categorías de plantillas
const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'Todas', icon: FiGrid },
  { id: 'landing', name: 'Landing Pages', icon: FiGlobe },
  { id: 'portfolio', name: 'Portfolio', icon: FiImage },
  { id: 'blog', name: 'Blog', icon: FiEdit3 },
  { id: 'ecommerce', name: 'E-commerce', icon: FiSquare },
  { id: 'corporate', name: 'Corporativo', icon: FiSettings },
  { id: 'creative', name: 'Creativo', icon: FiDroplet }
];

// Componente modal para la biblioteca de plantillas
function TemplateLibraryModal({ isOpen, onClose, onSelectTemplate }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Filtrar plantillas por categoría y búsqueda
  const filteredTemplates = TEMPLATE_LIBRARY.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
      setSelectedTemplate(null);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedTemplate(null);
    setSearchTerm('');
    setSelectedCategory('all');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden pb-16">
        {/* Header del modal */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Biblioteca de Plantillas</h2>
              <p className="text-purple-100">Elige una plantilla para comenzar tu proyecto</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Cerrar"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex h-[75vh]">
          {/* Sidebar con filtros */}
          <div className="w-80 bg-gray-50 border-r border-gray-200 p-8">
            {/* Barra de búsqueda */}
            <div className="mb-6">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar plantillas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtros por categoría */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FiFilter className="w-4 h-4" />
                Categorías
              </h3>
              <div className="space-y-1">
                {TEMPLATE_CATEGORIES.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-purple-100 text-purple-700 border border-purple-200'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      {category.name}
                      <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
                        selectedCategory === category.id
                          ? 'bg-purple-200 text-purple-700'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {category.id === 'all' 
                          ? TEMPLATE_LIBRARY.length 
                          : TEMPLATE_LIBRARY.filter(t => t.category === category.id).length
                        }
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Información de la plantilla seleccionada */}
            {selectedTemplate && (
              <div className="mt-6 p-6 bg-white rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">{selectedTemplate.name}</h4>
                <p className="text-sm text-gray-600 mb-4">{selectedTemplate.description}</p>
                <div className="flex flex-wrap gap-1">
                  {selectedTemplate.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Grid de plantillas */}
          <div className="flex-1 p-8 overflow-y-auto">
            <div className="mb-4">
              <p className="text-gray-600">
                Mostrando {filteredTemplates.length} plantilla{filteredTemplates.length !== 1 ? 's' : ''}
                {selectedCategory !== 'all' && ` en ${TEMPLATE_CATEGORIES.find(c => c.id === selectedCategory)?.name}`}
                {searchTerm && ` para "${searchTerm}"`}
              </p>
            </div>

            {filteredTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <FiSearch className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-lg mb-2">No se encontraron plantillas</p>
                <p className="text-sm">Intenta con otros términos de búsqueda o cambia la categoría</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`group cursor-pointer bg-white rounded-lg border-2 transition-all duration-200 hover:shadow-lg ${
                      selectedTemplate?.id === template.id
                        ? 'border-purple-400 shadow-md ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {/* Preview de la plantilla */}
                    <div className="aspect-video bg-gray-100 rounded-t-lg overflow-hidden">
                      <img
                        src={template.preview}
                        alt={template.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    </div>

                    {/* Información de la plantilla */}
                    <div className="p-4">
                      <div onClick={() => handleSelectTemplate(template)}>
                        <h3 className="font-semibold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
                          {template.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {template.description}
                        </p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {template.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                          {template.tags.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{template.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Botón Usar Plantilla - solo visible cuando está seleccionada */}
                      {selectedTemplate?.id === template.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectTemplate(template);
                            onClose();
                          }}
                          className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center justify-center gap-2 text-sm mt-3 animate-fade-in"
                        >
                          <FiCheck className="w-4 h-4" />
                          Usar esta Plantilla
                        </button>
                      )}
                    </div>

                    {/* Indicador de selección */}
                    {selectedTemplate?.id === template.id && (
                      <div className="absolute top-2 right-2 bg-purple-600 text-white p-1 rounded-full">
                        <FiCheck className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Componente para el sistema de plantillas del canvas
function CanvasTemplateSystem({ onAddContainerStructure, onLoadTemplate, onUploadTemplate }) {
  const [isStructuresOpen, setIsStructuresOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Generar estructura de contenedor único
  const createSingleContainer = () => {
    return {
      id: 'single-container',
      type: ELEMENT_TYPES.CONTAINER,
      name: 'Contenedor Simple',
      defaultProps: {
        layout: 'vertical',
        gap: '20px',
        padding: '24px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: 'none',
        minHeight: '200px',
        children: [],
        widthType: 'full',
      }
    };
  };

  // Generar estructura de dos contenedores horizontales
  const createDoubleContainer = () => {
    return {
      id: 'double-container',
      type: ELEMENT_TYPES.CONTAINER,
      name: 'Dos Contenedores',
      defaultProps: {
        layout: 'horizontal',
        gap: '20px',
        padding: '24px',
        backgroundColor: 'transparent',
        borderRadius: '0px',
        border: 'none',
        minHeight: '200px',
        widthType: 'full',
        // Propiedades responsives para el contenedor padre
        responsive: {
          [VIEWPORT_MODES.DESKTOP]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: '24px',
            padding: '24px',
          },
          [VIEWPORT_MODES.TABLET]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: '18px',
            padding: '20px',
          },
          [VIEWPORT_MODES.MOBILE]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: '16px',
            padding: '16px',
          }
        },
        // Propiedades legacy para compatibilidad
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        flexWrap: 'nowrap',
        columnGap: '20px',
        rowGap: '20px',
        children: [
          {
            id: `container-left-${Date.now()}`,
            type: ELEMENT_TYPES.CONTAINER,
            props: {
              layout: 'vertical',
              gap: '16px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: 'none',
              minHeight: '160px',
              children: [],
              widthType: 'full',
              // Propiedades responsives para adaptar a cada viewport
              responsive: {
                [VIEWPORT_MODES.DESKTOP]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '20px',
                  padding: '24px',
                },
                [VIEWPORT_MODES.TABLET]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '16px',
                  padding: '20px',
                },
                [VIEWPORT_MODES.MOBILE]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '12px',
                  padding: '16px',
                }
              },
              // Propiedades legacy para compatibilidad
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              columnGap: '16px',
              rowGap: '16px',
            }
          },
          {
            id: `container-right-${Date.now() + 1}`,
            type: ELEMENT_TYPES.CONTAINER,
            props: {
              layout: 'vertical',
              gap: '16px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: 'none',
              minHeight: '160px',
              children: [],
              widthType: 'full',
              // Propiedades responsives para adaptar a cada viewport
              responsive: {
                [VIEWPORT_MODES.DESKTOP]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '20px',
                  padding: '24px',
                },
                [VIEWPORT_MODES.TABLET]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '16px',
                  padding: '20px',
                },
                [VIEWPORT_MODES.MOBILE]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '12px',
                  padding: '16px',
                }
              },
              // Propiedades legacy para compatibilidad
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              columnGap: '16px',
              rowGap: '16px',
            }
          }
        ],
      }
    };
  };

  // Generar estructura de tres contenedores horizontales
  const createTripleContainer = () => {
    return {
      id: 'triple-container',
      type: ELEMENT_TYPES.CONTAINER,
      name: 'Tres Contenedores',
      defaultProps: {
        layout: 'horizontal',
        gap: '16px',
        padding: '24px',
        backgroundColor: 'transparent',
        borderRadius: '0px',
        border: 'none',
        minHeight: '200px',
        widthType: 'full',
        // Propiedades responsives para el contenedor padre
        responsive: {
          [VIEWPORT_MODES.DESKTOP]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: '20px',
            padding: '24px',
          },
          [VIEWPORT_MODES.TABLET]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: '16px',
            padding: '20px',
          },
          [VIEWPORT_MODES.MOBILE]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: '12px',
            padding: '16px',
          }
        },
        // Propiedades legacy para compatibilidad
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        flexWrap: 'nowrap',
        columnGap: '16px',
        rowGap: '16px',
        children: [
          {
            id: `container-left-${Date.now()}`,
            type: ELEMENT_TYPES.CONTAINER,
            props: {
              layout: 'vertical',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: 'none',
              minHeight: '140px',
              children: [],
              widthType: 'full',
              // Propiedades responsives para adaptar a cada viewport
              responsive: {
                [VIEWPORT_MODES.DESKTOP]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '16px',
                  padding: '20px',
                },
                [VIEWPORT_MODES.TABLET]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '12px',
                  padding: '16px',
                },
                [VIEWPORT_MODES.MOBILE]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '8px',
                  padding: '12px',
                }
              },
              // Propiedades legacy para compatibilidad
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              columnGap: '12px',
              rowGap: '12px',
            }
          },
          {
            id: `container-center-${Date.now() + 1}`,
            type: ELEMENT_TYPES.CONTAINER,
            props: {
              layout: 'vertical',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: 'none',
              minHeight: '140px',
              children: [],
              widthType: 'full',
              // Propiedades responsives para adaptar a cada viewport
              responsive: {
                [VIEWPORT_MODES.DESKTOP]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '16px',
                  padding: '20px',
                },
                [VIEWPORT_MODES.TABLET]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '12px',
                  padding: '16px',
                },
                [VIEWPORT_MODES.MOBILE]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '8px',
                  padding: '12px',
                }
              },
              // Propiedades legacy para compatibilidad
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              columnGap: '12px',
              rowGap: '12px',
            }
          },
          {
            id: `container-right-${Date.now() + 2}`,
            type: ELEMENT_TYPES.CONTAINER,
            props: {
              layout: 'vertical',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: 'none',
              minHeight: '140px',
              children: [],
              widthType: 'full',
              // Propiedades responsives para adaptar a cada viewport
              responsive: {
                [VIEWPORT_MODES.DESKTOP]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '16px',
                  padding: '20px',
                },
                [VIEWPORT_MODES.TABLET]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '12px',
                  padding: '16px',
                },
                [VIEWPORT_MODES.MOBILE]: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  flexWrap: 'nowrap',
                  gap: '8px',
                  padding: '12px',
                }
              },
              // Propiedades legacy para compatibilidad
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              columnGap: '12px',
              rowGap: '12px',
            }
          }
        ],
      }
    };
  };

  const handleStructureClick = (structureType) => {
    let structure;
    switch (structureType) {
      case CONTAINER_STRUCTURES.SINGLE:
        structure = createSingleContainer();
        break;
      case CONTAINER_STRUCTURES.DOUBLE:
        structure = createDoubleContainer();
        break;
      case CONTAINER_STRUCTURES.TRIPLE:
        structure = createTripleContainer();
        break;
      default:
        return;
    }
    
    console.log('🏗️ Adding container structure:', structureType, structure);
    onAddContainerStructure(structure);
    setIsStructuresOpen(false); // Cerrar el panel después de seleccionar
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const template = JSON.parse(e.target.result);
          console.log('📁 Uploaded template:', template);
          onUploadTemplate(template);
        } catch (error) {
          console.error('❌ Error parsing JSON template:', error);
          alert('Error al leer el archivo JSON. Verifica que el formato sea correcto.');
        }
      };
      reader.readAsText(file);
    } else {
      alert('Por favor selecciona un archivo JSON válido.');
    }
    
    // Reset input
    event.target.value = '';
  };

  return (
    <div className="mt-6 border-t border-gray-200 pt-6">
      {/* Tres iconos principales */}
      <div className="flex items-center justify-center gap-4 mb-4">
        {/* Icono de Estructuras */}
        <div className="relative">
          <button
            onClick={() => setIsStructuresOpen(!isStructuresOpen)}
            className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed transition-all duration-200 group ${
              isStructuresOpen 
                ? 'border-purple-400 bg-purple-50 shadow-md' 
                : 'border-gray-300 bg-white hover:border-purple-300 hover:bg-purple-25 hover:shadow-sm'
            }`}
            title="Estructuras de contenedores"
          >
            <FiPlus className={`w-6 h-6 mb-2 transition-colors ${
              isStructuresOpen ? 'text-purple-600' : 'text-gray-600 group-hover:text-purple-500'
            }`} />
            <span className={`text-xs font-medium transition-colors ${
              isStructuresOpen ? 'text-purple-700' : 'text-gray-600 group-hover:text-purple-600'
            }`}>
              Estructuras
            </span>
          </button>
        </div>

        {/* Icono de Biblioteca */}
        <button
          onClick={() => setIsLibraryOpen(true)}
          className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-25 hover:shadow-sm transition-all duration-200 group"
          title="Cargar plantilla de la biblioteca"
        >
          <FiFolder className="w-6 h-6 mb-2 text-gray-600 group-hover:text-blue-500 transition-colors" />
          <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
            Biblioteca
          </span>
        </button>

        {/* Icono de Subir JSON */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-gray-300 bg-white hover:border-green-300 hover:bg-green-25 hover:shadow-sm transition-all duration-200 group"
          title="Subir plantilla JSON"
        >
          <FiUpload className="w-6 h-6 mb-2 text-gray-600 group-hover:text-green-500 transition-colors" />
          <span className="text-xs font-medium text-gray-600 group-hover:text-green-600 transition-colors">
            Subir JSON
          </span>
        </button>
        
        {/* Input oculto para archivos */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {/* Panel desplegable de estructuras */}
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isStructuresOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-purple-700 mb-4 text-center">Elige una estructura</h4>
          <div className="grid grid-cols-3 gap-4">
            {/* Contenedor único */}
            <button
              onClick={() => handleStructureClick(CONTAINER_STRUCTURES.SINGLE)}
              className="flex flex-col items-center p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 hover:shadow-md transition-all duration-200 group"
              title="Agregar contenedor único"
            >
              <div className="w-10 h-8 bg-gray-200 rounded border group-hover:bg-purple-200 mb-3 transition-colors"></div>
              <span className="text-xs text-gray-600 group-hover:text-purple-700 font-medium transition-colors">
                1 Contenedor
              </span>
            </button>

            {/* Dos contenedores */}
            <button
              onClick={() => handleStructureClick(CONTAINER_STRUCTURES.DOUBLE)}
              className="flex flex-col items-center p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 hover:shadow-md transition-all duration-200 group"
              title="Agregar dos contenedores lado a lado"
            >
              <div className="flex gap-1 mb-3">
                <div className="w-5 h-8 bg-gray-200 rounded border group-hover:bg-purple-200 transition-colors"></div>
                <div className="w-5 h-8 bg-gray-200 rounded border group-hover:bg-purple-200 transition-colors"></div>
              </div>
              <span className="text-xs text-gray-600 group-hover:text-purple-700 font-medium transition-colors">
                2 Contenedores
              </span>
            </button>

            {/* Tres contenedores */}
            <button
              onClick={() => handleStructureClick(CONTAINER_STRUCTURES.TRIPLE)}
              className="flex flex-col items-center p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 hover:shadow-md transition-all duration-200 group"
              title="Agregar tres contenedores lado a lado"
            >
              <div className="flex gap-1 mb-3">
                <div className="w-3 h-8 bg-gray-200 rounded border group-hover:bg-purple-200 transition-colors"></div>
                <div className="w-3 h-8 bg-gray-200 rounded border group-hover:bg-purple-200 transition-colors"></div>
                <div className="w-3 h-8 bg-gray-200 rounded border group-hover:bg-purple-200 transition-colors"></div>
              </div>
              <span className="text-xs text-gray-600 group-hover:text-purple-700 font-medium transition-colors">
                3 Contenedores
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de biblioteca de plantillas */}
      <TemplateLibraryModal
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelectTemplate={(template) => {
          console.log('📚 Template selected from library:', template.name);
          // Cargar todos los elementos de la plantilla al canvas
          template.elements.forEach(element => {
            onAddContainerStructure(element);
          });
        }}
      />
    </div>
  );
}

// Componente selector de modo de visualización
function ViewportSelector({ currentMode, onModeChange }) {
  return (
    <div className="flex items-center gap-1 bg-[#2a2a2a] rounded-lg p-1">
      {Object.entries(VIEWPORT_CONFIGS).map(([mode, config]) => {
        const IconComponent = config.icon;
        return (
          <button
            key={mode}
            onClick={() => onModeChange(mode)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              currentMode === mode
                ? 'bg-[#ff1b6d] text-white'
                : 'text-gray-300 hover:bg-[#3a3a3a] hover:text-white'
            }`}
            title={`Vista ${config.name}`}
          >
            <IconComponent className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
}


// Función para convertir elementos a HTML para vista previa
function renderElementToHTML(element) {
  switch (element.type) {
    case ELEMENT_TYPES.CONTAINER:
      const childrenHTML = element.props.children ? 
        element.props.children.map(child => renderElementToHTML(child)).join('') : '';
      
      // Determinar la altura correcta: usar height si está definido, sino minHeight
      const containerHeight = element.props.height && element.props.height !== 'auto' 
        ? element.props.height 
        : (element.props.minHeight || '100px');
      
      return `
        <div style="
          display: flex;
          flex-direction: ${element.props.flexDirection || 'column'};
          gap: ${element.props.gap || '16px'};
          padding: ${element.props.padding || '20px'};
          background-color: ${element.props.backgroundColor === 'transparent' ? 'transparent' : (element.props.backgroundColor || 'transparent')};
          background-image: ${element.props.backgroundImage || 'none'};
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          border-radius: ${element.props.borderRadius || '0px'};
          border: ${element.props.border && element.props.border !== '2px dashed #e2e8f0' ? element.props.border : 'none'};
          width: ${element.props.widthType === 'full' ? '100%' : (element.props.width || 'auto')};
          height: ${containerHeight};
          align-items: ${element.props.alignItems || 'stretch'};
          justify-content: ${element.props.justifyContent || 'flex-start'};
          flex-wrap: ${element.props.flexWrap || 'nowrap'};
          ${element.props.glassEffect ? `backdrop-filter: blur(${element.props.glassBlur || 10}px); -webkit-backdrop-filter: blur(${element.props.glassBlur || 10}px);` : ''}
          ${element.props.glassEffect ? `background-color: ${element.props.glassColor || '#ffffff'}${Math.round((element.props.glassOpacity || 20) * 2.55).toString(16).padStart(2, '0')};` : ''}
          box-sizing: border-box;
        ">
          ${childrenHTML}
        </div>
      `;
    case ELEMENT_TYPES.HEADING:
      return `
        <h${element.props.level || 1} style="
          color: ${element.props.color || '#000000'};
          font-size: ${element.props.fontSize || '32px'};
          text-align: ${element.props.alignment || 'left'};
          margin: 0;
        ">
          ${element.props.text || ''}
        </h${element.props.level || 1}>
      `;
    case ELEMENT_TYPES.TEXT:
      return `
        <p style="
          color: ${element.props.color || '#333333'};
          font-size: ${element.props.fontSize || '16px'};
          text-align: ${element.props.alignment || 'left'};
          margin: 0;
        ">
          ${element.props.text || ''}
        </p>
      `;
    case ELEMENT_TYPES.IMAGE:
      return `
        <img 
          src="${element.props.src || '/api/placeholder/400/300'}" 
          alt="${element.props.alt || 'Imagen'}" 
          style="
            width: ${element.props.width || '100%'};
            height: ${element.props.height || 'auto'};
            object-fit: cover;
          "
        />
      `;
    case ELEMENT_TYPES.BUTTON:
      return `
        <a 
          href="${element.props.link || '#'}" 
          style="
            display: inline-block;
            padding: ${element.props.padding || '12px 24px'};
            background-color: ${element.props.backgroundColor || '#8b5cf6'};
            background-image: ${element.props.backgroundImage || 'none'};
            background-size: cover;
            background-position: center;
            color: ${element.props.textColor || '#ffffff'};
            text-decoration: none;
            border-radius: ${element.props.borderRadius || '8px'};
            font-family: inherit;
            cursor: pointer;
          "
        >
          ${element.props.text || 'Botón'}
        </a>
      `;
    case ELEMENT_TYPES.VIDEO:
      return `
        <video 
          src="${element.props.src || ''}" 
          style="
            width: ${element.props.width || '100%'};
            height: ${element.props.height || '315px'};
          "
          controls
        ></video>
      `;
    default:
      return `<div>Elemento: ${element.type}</div>`;
  }
}

function Editor({ selectedTemplate, onExit }) {
  const navigate = useNavigate();
  const [selectedElement, setSelectedElement] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [viewportMode, setViewportMode] = useState(VIEWPORT_MODES.DESKTOP);
  const [projectName, setProjectName] = useState(selectedTemplate ? selectedTemplate.name : 'Lienzo en Blanco');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  
  // Sistema de historial
  const {
    saveState,
    undo,
    redo,
    canUndo,
    canRedo,
    getCurrentState,
    historyLength,
    currentIndex
  } = useHistory([]);
  
  // Estado de elementos usando el historial
  const [elements, setElementsInternal] = useState([]);
  const [isUndoRedoOperation, setIsUndoRedoOperation] = useState(false);
  // Referencia a la ventana de vista previa
  const previewWindowRef = useRef(null);

  // Función para generar el HTML completo de la vista previa
  const generatePreviewHTML = useCallback((elementsData, title) => {
    const contentHTML = elementsData.map(element => renderElementToHTML(element)).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Vista Previa - ${title}</title>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif; 
            padding: 0; 
            margin: 0; 
            line-height: 1.6;
          }
          * {
            box-sizing: border-box;
          }
        </style>
      </head>
      <body>
        <div id='content'>
          ${contentHTML}
        </div>
      </body>
      </html>
    `;
  }, []);

  // Función para actualizar la vista previa (reutiliza la misma pestaña)
  const updatePreviewWindow = useCallback(() => {
    // Si no hay una ventana de vista previa abierta, no hacer nada
    if (!previewWindowRef.current || previewWindowRef.current.closed) {
      return;
    }

    try {
      const htmlContent = generatePreviewHTML(elements, projectName);
      previewWindowRef.current.document.open();
      previewWindowRef.current.document.write(htmlContent);
      previewWindowRef.current.document.close();
    } catch (error) {
      console.error('Error actualizando vista previa:', error);
      // Si hay error, limpiar la referencia
      previewWindowRef.current = null;
    }
  }, [elements, projectName, generatePreviewHTML]);

  // Función para abrir ventana de vista previa con estado actual
  const openPreviewWindow = useCallback(() => {
    // Si ya hay una ventana abierta y no está cerrada, enfocarla y actualizarla
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      previewWindowRef.current.focus();
      updatePreviewWindow();
      return;
    }

    // Abrir nueva ventana de vista previa
    const previewTab = window.open('', 'aurelio-preview', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    
    if (previewTab) {
      previewWindowRef.current = previewTab;
      
      const htmlContent = generatePreviewHTML(elements, projectName);
      previewTab.document.write(htmlContent);
      previewTab.document.close();
      
      // Limpiar la referencia cuando se cierre la ventana
      previewTab.addEventListener('beforeunload', () => {
        previewWindowRef.current = null;
      });
    } else {
      alert('No se pudo abrir la pestaña de vista previa. Verifica que tu navegador permita ventanas emergentes.');
    }
  }, [elements, projectName, generatePreviewHTML, updatePreviewWindow]);

  // Efecto para actualizar automáticamente la vista previa cuando cambien los elementos
  useEffect(() => {
    // Solo actualizar si hay una ventana de vista previa abierta
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      // Usar un pequeño delay para evitar actualizaciones muy frecuentes
      const timeoutId = setTimeout(() => {
        updatePreviewWindow();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [elements, projectName, updatePreviewWindow]);

  // Función wrapper para setElements que maneja el historial
  const setElements = useCallback((updater) => {
    setElementsInternal(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      
      // Solo guardar en el historial si no es una operación de undo/redo
      if (!isUndoRedoOperation) {
        // Usar setTimeout para evitar problemas de timing con el estado
        setTimeout(() => {
          saveState(newState);
        }, 0);
      }
      
      return newState;
    });
  }, [isUndoRedoOperation, saveState]);

  // Inicializar el historial cuando sea necesario
  // Almacenar el estado actual en el almacenamiento local después de cada cambio
  useEffect(() => {
    localStorage.setItem('editorState', JSON.stringify(getCurrentState()));
  }, [elements]);

  // Recuperar el estado del editor al cargar
  useEffect(() => {
    const savedState = localStorage.getItem('editorState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (Array.isArray(parsedState) && parsedState.length > 0) {
          console.log('🔄 Loading saved state from localStorage:', parsedState);
          setElementsInternal(parsedState);
          saveState(parsedState);
          return;
        }
      } catch (error) {
        console.error('Error parsing saved state:', error);
      }
    }
    
    // Si no hay estado guardado válido, inicializar vacío
    console.log('🆕 Initializing empty state');
    saveState([]);
  }, [saveState]);

  // Función para hacer undo
  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState !== null) {
      setIsUndoRedoOperation(true);
      setElementsInternal(previousState);
      // Resetear la flag después de un breve delay
      setTimeout(() => setIsUndoRedoOperation(false), 10);
      console.log('⏪ Undo executed - State restored');
    }
  }, [undo]);

  // Función para hacer redo
  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState !== null) {
      setIsUndoRedoOperation(true);
      setElementsInternal(nextState);
      // Resetear la flag después de un breve delay
      setTimeout(() => setIsUndoRedoOperation(false), 10);
      console.log('⏩ Redo executed - State restored');
    }
  }, [redo]);

  // Atajos de teclado para Undo/Redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z o Cmd+Z para Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Y o Cmd+Shift+Z para Redo  
      else if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey)
      ) {
        e.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  console.log('🔧 Elements state:', elements.length, 'elements in canvas');
  console.log('📱 Current viewport mode:', viewportMode);
  console.log('📚 History stats:', { historyLength, currentIndex, canUndo, canRedo });

  const addElement = useCallback((elementTemplate) => {
    const newElement = {
      id: `${elementTemplate.type}-${Date.now()}`,
      type: elementTemplate.type,
      props: { ...elementTemplate.defaultProps },
    };
    setElements((prev) => [...prev, newElement]);
  }, [setElements]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = useCallback((result) => {
    console.log('🚀 Drag end:', result);
    
    const { destination, source, draggableId } = result;

    // Si no hay destino, no hacer nada
    if (!destination) {
      console.log('❌ No destination found');
      return;
    }

    // Si es un elemento del panel siendo arrastrado al canvas
    if (source.droppableId === 'panel' && destination.droppableId === 'canvas') {
      console.log('🎯 Panel element being dragged to canvas');
      const elementId = draggableId.replace('panel-', '');
      const elementTemplate = availableElements.find(el => el.id === elementId);
      console.log('Found template:', elementTemplate?.name);
      
      if (elementTemplate) {
        console.log('✅ Adding element to canvas');
        addElement(elementTemplate);
      } else {
        console.log('❌ Element template not found for:', elementId);
      }
      return;
    }

    // Si es reordenamiento dentro del canvas
    if (
      source.droppableId === destination.droppableId &&
      source.droppableId === 'canvas'
    ) {
      console.log('🔄 Reordering elements in canvas');
      const newElements = reorder(
        elements,
        source.index,
        destination.index
      );
      setElements(newElements);
      return;
    }
  }, [elements, addElement]);

  // Función para eliminar elementos (tanto del canvas principal como de contenedores)
  const deleteElement = useCallback((elementId, containerId = null) => {
    console.log('🗑️ deleteElement called with:', {
      elementId,
      containerId,
      hasContainerId: containerId !== null,
      currentElementsCount: elements.length
    });
    
    // Función recursiva para encontrar y eliminar el elemento en cualquier nivel
    const findAndDeleteElement = (elements, targetId) => {
      return elements.reduce((acc, element) => {
        if (element.id === targetId) {
          console.log('🎯 Found element to delete:', targetId, 'Type:', element.type);
          return acc; // No incluir este elemento en el resultado
        }
        
        if (element.props?.children) {
          const updatedChildren = findAndDeleteElement(element.props.children, targetId);
          return [...acc, {
            ...element,
            props: {
              ...element.props,
              children: updatedChildren
            }
          }];
        }
        
        return [...acc, element];
      }, []);
    };
    
    if (containerId) {
      console.log('🎯 Deleting element from container:', containerId);
      // Eliminar elemento de un contenedor específico (buscar recursivamente)
      setElements((prev) => {
        console.log('📋 Current elements before deletion:', prev);
        const updated = updateNestedElement(prev, containerId, (container) => {
          console.log('🔍 Found container to delete from:', container.id, 'Children before:', container.props.children?.length || 0);
          console.log('🔍 Looking for child with ID (recursively):', elementId);
          console.log('🔍 Current children structure:', container.props.children?.map(c => ({ id: c.id, type: c.type })));
          
          const childrenBefore = container.props.children || [];
          const childrenAfter = findAndDeleteElement(childrenBefore, elementId);
          
          console.log('🔄 Children before deletion:', childrenBefore.length);
          console.log('🔄 Children after deletion:', childrenAfter.length);
          console.log('🔄 Deletion successful:', childrenBefore.length !== childrenAfter.length);
          
          const updatedContainer = {
            ...container,
            props: {
              ...container.props,
              children: childrenAfter
            }
          };
          console.log('✅ Container after deletion:', updatedContainer.id, 'Children after:', updatedContainer.props.children?.length || 0);
          console.log('✅ Final children IDs:', updatedContainer.props.children?.map(c => c.id));
          return updatedContainer;
        });
        console.log('📋 Elements after container deletion:', updated);
        return updated;
      });
    } else {
      console.log('🎯 Deleting element from main canvas or searching globally');
      // Eliminar elemento del canvas principal o buscar globalmente
      setElements((prev) => {
        const updated = findAndDeleteElement(prev, elementId);
        console.log('📋 Elements after global deletion:', updated);
        return updated;
      });
    }
    
    if (selectedElement?.id === elementId) {
      console.log('🔄 Clearing selected element');
      setSelectedElement(null);
    }
  }, [selectedElement, elements]);

  // Función para duplicar elementos (tanto del canvas principal como de contenedores)
  const duplicateElement = useCallback((element, containerId = null) => {
    console.log('🚀 duplicateElement called:', {
      elementId: element.id,
      elementType: element.type,
      containerId: containerId,
      isInContainer: containerId !== null,
      elementProps: element.props
    });
    
    // Función para buscar un elemento por ID en la estructura
    const findElementById = (elements, targetId) => {
      for (const el of elements) {
        if (el.id === targetId) return el;
        if (el.props?.children) {
          const found = findElementById(el.props.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const newElement = {
      ...element,
      id: `${element.type}-${Date.now()}`,
      props: {
        ...element.props,
        // Si es un contenedor, también duplicar sus hijos con nuevos IDs
        children: element.props?.children ? element.props.children.map(child => ({
          ...child,
          id: `${child.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          props: { ...child.props }
        })) : element.props?.children
      }
    };
    
    console.log('📋 Created new element:', {
      originalId: element.id,
      newId: newElement.id,
      type: newElement.type,
      hasChildren: !!newElement.props?.children?.length
    });

    if (containerId) {
      console.log('🎯 Duplicating element within container:', containerId);
      // Duplicar elemento dentro de un contenedor específico
      setElements((prev) => {
        console.log('📋 Current elements before duplicate in container:', prev.length);
        
        const updated = updateNestedElement(prev, containerId, (container) => {
          console.log('🔍 Found container for duplication:', {
            containerId: container.id,
            currentChildrenCount: container.props.children?.length || 0
          });
          
          const currentChildren = container.props.children || [];
          const newChildren = [...currentChildren, newElement];
          const totalChildren = newChildren.length;
          
          console.log('📊 Container duplication stats:', {
            beforeCount: currentChildren.length,
            afterCount: totalChildren,
            newElementId: newElement.id
          });
          
          // Verificar si el contenedor es horizontal y tiene múltiples hijos
          const isHorizontal = container.props.flexDirection === 'row' || container.props.flexDirection === 'row-reverse';
          const hasMultipleChildren = totalChildren > 1;

          if (isHorizontal && hasMultipleChildren) {
            // En contenedores horizontales, no necesitamos cambiar width
            // El sistema de flexbox con flex-1 ya maneja la distribución equitativa
            // Solo nos aseguramos de que los elementos no tengan width fijo que interfiera
            console.log('🔄 Horizontal container with multiple children - using flexbox distribution:', {
              containerId: container.id,
              totalChildren,
              containerDirection: container.props.flexDirection
            });
            
            // Para contenedores horizontales, eliminar cualquier width fijo en los hijos
            // para que flex-1 funcione correctamente
            const childrenWithFlexLayout = newChildren.map(child => {
              // Solo remover width si es un contenedor que podría interferir con flex
              if (child.type === 'container' && child.props.width && child.props.width !== '100%') {
                return {
                  ...child,
                  props: {
                    ...child.props,
                    widthType: 'full', // Cambiar a ancho completo para que funcione con flex
                    width: '100%'
                  }
                };
              }
              return child;
            });
            
            const finalContainer = {
              ...container,
              props: {
                ...container.props,
                children: childrenWithFlexLayout
              }
            };
            
            console.log('✅ Updated horizontal container:', {
              containerId: finalContainer.id,
              childrenCount: finalContainer.props.children.length,
              childrenIds: finalContainer.props.children.map(c => c.id)
            });
            
            return finalContainer;
          } else {
            // Para contenedores verticales o con un solo hijo, no cambiar nada
            const finalContainer = {
              ...container,
              props: {
                ...container.props,
                children: newChildren
              }
            };
            
            console.log('✅ Updated vertical container:', {
              containerId: finalContainer.id,
              childrenCount: finalContainer.props.children.length,
              childrenIds: finalContainer.props.children.map(c => c.id)
            });
            
            return finalContainer;
          }
        });
        
        console.log('📋 Elements after container duplication:', updated.length);
        return updated;
      });
    } else {
      console.log('🎯 Duplicating element in main canvas');
      // Duplicar elemento en el canvas principal
      setElements((prev) => {
        const updated = [...prev, newElement];
        console.log('📋 Elements after canvas duplication:', {
          beforeCount: prev.length,
          afterCount: updated.length,
          newElementId: newElement.id
        });
        return updated;
      });
    }
    
    console.log('✅ duplicateElement completed successfully');
  }, [elements]);

  // Función para actualizar elementos (tanto del canvas principal como anidados)
  const updateElement = useCallback((updatedElement) => {
    console.log('🚀 updateElement called with:', {
      elementId: updatedElement.id,
      elementType: updatedElement.type,
      updatedProps: updatedElement.props,
      currentElementsCount: elements.length
    });
    
    // Función recursiva para buscar y actualizar el elemento
    const updateElementRecursively = (elements) => {
      return elements.map(el => {
        if (el.id === updatedElement.id) {
          console.log('✅ Found element to update:', el.id, 'Replacing with:', updatedElement.id);
          return updatedElement;
        }
        if (el.props?.children) {
          return {
            ...el,
            props: {
              ...el.props,
              children: updateElementRecursively(el.props.children)
            }
          };
        }
        return el;
      });
    };
    
    console.log('🔄 About to update elements state...');
    setElements((prev) => {
      const updated = updateElementRecursively(prev);
      console.log('✅ Elements updated successfully. New state:', updated);
      return updated;
    });
    setSelectedElement(updatedElement);
    console.log('✅ Selected element updated');
  }, []);

  // Función para buscar y actualizar elementos anidados recursivamente
  const updateNestedElement = (elements, targetId, updater) => {
    return elements.map(element => {
      if (element.id === targetId) {
        return updater(element);
      }
      if (element.props?.children) {
        return {
          ...element,
          props: {
            ...element.props,
            children: updateNestedElement(element.props.children, targetId, updater)
          }
        };
      }
      return element;
    });
  };

  // Función para agregar elementos a contenedores
  const addToContainer = useCallback((containerId, elementTemplate) => {
    console.log('🔥 addToContainer called:', {
      containerId,
      elementType: elementTemplate.type,
      elementName: elementTemplate.name
    });
    
    const newElement = {
      id: `${elementTemplate.type}-${Date.now()}`,
      type: elementTemplate.type,
      props: { ...elementTemplate.defaultProps },
    };
    
    console.log('✨ Created new element:', newElement);

    setElements((prev) => {
      console.log('📋 Current elements before update:', prev);
      
      const updated = updateNestedElement(prev, containerId, (container) => {
        console.log('🎯 Found container to update:', container.id, 'Current children:', container.props.children?.length || 0);
        
        const updatedContainer = {
          ...container,
          props: {
            ...container.props,
            children: [...(container.props.children || []), newElement]
          }
        };
        
        console.log('✅ Updated container children count:', updatedContainer.props.children.length);
        return updatedContainer;
      });
      
      console.log('📋 Elements after update:', updated);
      return updated;
    });
  }, []);

  // Función para mover elementos existentes del canvas a contenedores
  const moveToContainer = useCallback((elementId, containerId) => {
    console.log('🚚 moveToContainer called:', {
      elementId,
      containerId
    });

    // Función recursiva para encontrar el elemento en cualquier nivel
    const findAndRemoveElement = (elements, targetId) => {
      let foundElement = null;
      
      const updatedElements = elements.reduce((acc, element) => {
        if (element.id === targetId) {
          foundElement = element;
          return acc; // No incluir este elemento en el resultado
        }
        
        if (element.props?.children) {
          const result = findAndRemoveElement(element.props.children, targetId);
          if (result.foundElement) {
            foundElement = result.foundElement;
          }
          return [...acc, {
            ...element,
            props: {
              ...element.props,
              children: result.elements
            }
          }];
        }
        
        return [...acc, element];
      }, []);
      
      return { elements: updatedElements, foundElement };
    };

    setElements((prev) => {
      console.log('📋 Current elements before move:', prev);
      
      // 1. Encontrar y remover el elemento de su posición actual
      const { elements: elementsAfterRemoval, foundElement } = findAndRemoveElement(prev, elementId);
      
      if (!foundElement) {
        console.error('❌ Element not found:', elementId);
        return prev;
      }
      
      console.log('✅ Found element to move:', foundElement);
      console.log('📋 Elements after removal:', elementsAfterRemoval);
      
      // 2. Si containerId es null, mover al canvas principal
      if (containerId === null) {
        console.log('🎯 Moving element to main canvas');
        const updated = [...elementsAfterRemoval, foundElement];
        console.log('📋 Final elements after move to canvas:', updated);
        return updated;
      }
      
      // 3. Si no, agregar el elemento al contenedor de destino
      const updated = updateNestedElement(elementsAfterRemoval, containerId, (container) => {
        console.log('🎯 Adding element to container:', container.id);
        
        const updatedContainer = {
          ...container,
          props: {
            ...container.props,
            children: [...(container.props.children || []), foundElement]
          }
        };
        
        console.log('✅ Container updated with new child');
        return updatedContainer;
      });
      
      console.log('📋 Final elements after move:', updated);
      return updated;
    });
  }, []);

  const handleSave = () => {
    // Aquí implementarías la lógica de guardado
    console.log('Guardando página...', elements);
    alert('Página guardada correctamente');
  };

  const handlePublish = () => {
    // Aquí implementarías la lógica de publicación
    console.log('Publicando página...', elements);
    alert('Página publicada correctamente');
  };

  if (isPreviewMode) {
    return (
      <div className="min-h-screen bg-white">
        {/* Preview Mode */}
        <div className="fixed top-4 left-4 z-50">
          <button
            onClick={() => setIsPreviewMode(false)}
            className="bg-[#8b5cf6] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#7c3aed] transition-colors"
          >
            <FiEdit3 className="w-4 h-4" />
            Volver al Editor
          </button>
        </div>
        <div className="pt-16">
          {elements.map((element) => (
            <div key={element.id} className="mb-4">
              <SortableElement
                element={element}
                isSelected={false}
                onSelect={() => {}}
                onDelete={() => {}}
                onDuplicate={() => {}}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex flex-col">
      {/* Toolbar superior */}
      <div className="bg-[#1a1a1a] border-b border-[#2a2a2a] flex items-center relative">
        {/* Contenido principal con padding */}
        <div className="flex items-center w-full p-4 pr-36">
          {/* Sección izquierda: Logo, Botón volver y título */}
          <div className="flex items-center gap-4 flex-1">
            {/* Logo de Wemax */}
            <div className="flex-shrink-0">
              <img 
                src="/wemax-logo.png" 
                alt="Wemax Logo" 
                className="w-8 h-8 object-contain"
              />
            </div>
            
            <button
              onClick={onExit}
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            >
              <FiArrowLeft className="w-4 h-4" />
              Volver
            </button>
            <div className="text-white group">
              {isEditingTitle ? (
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onBlur={() => setIsEditingTitle(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsEditingTitle(false);
                    }
                  }}
                  className="bg-transparent font-semibold border-none outline-none focus:ring-2 focus:ring-purple-500 px-2 py-1 rounded"
                  autoFocus
                  placeholder="Nombre del proyecto"
                />
              ) : (
                <div 
                  className="flex items-center gap-2 cursor-pointer hover:bg-white/10 px-2 py-1 rounded transition-colors"
                  onClick={() => setIsEditingTitle(true)}
                  title="Haz clic para editar el nombre"
                >
                  <h1 className="font-semibold">
                    {projectName}
                  </h1>
                  <FiEdit3 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>
          </div>
          
          {/* Sección central: Selector de viewport - Centrado absoluto */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <ViewportSelector 
              currentMode={viewportMode} 
              onModeChange={setViewportMode} 
            />
          </div>
          
          {/* Sección derecha: Botones Guardar y Vista Previa (justo antes del botón Publicar) */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="flex items-center justify-center w-8 h-8 text-gray-300 hover:text-white transition-colors"
              title="Guardar"
            >
              <FiSave className="w-4 h-4" />
            </button>
            <button
              onClick={openPreviewWindow}
              className="flex items-center justify-center w-8 h-8 text-gray-300 hover:text-white transition-colors"
              title="Vista Previa"
            >
              <FiEye className="w-4 h-4 flex-shrink-0" />
            </button>
          </div>
        </div>
        
        {/* Botón Publicar - Altura completa en el borde derecho */}
        <button
          onClick={handlePublish}
          className="absolute right-0 top-0 bottom-0 flex items-center justify-center gap-2 bg-[#ff1b6d] text-white px-6 hover:bg-[#e0195f] transition-colors border-l border-[#2a2a2a]"
          title="Publicar página"
        >
          <FiGlobe className="w-4 h-4" />
          Publicar
        </button>
      </div>

      {/* Editor principal - SIN restricciones de altura */}
      <div className="flex" style={{ height: 'calc(100vh - 80px)' }}>
        {/* Panel de elementos */}
        <ElementsPanel onAddElement={addElement} />

        {/* Canvas central con scroll infinito real */}
        <div className="flex-1">
          <InfiniteCanvas 
            elements={elements} 
            selectedElement={selectedElement} 
            onSelectElement={setSelectedElement} 
            onDeleteElement={deleteElement} 
            onDuplicateElement={duplicateElement} 
            onAddElement={addElement}
            onReorderElements={(fromIndex, toIndex) => {
              const newElements = reorder(elements, fromIndex, toIndex);
              setElements(newElements);
            }}
            onAddToContainer={addToContainer}
            onMoveToContainer={moveToContainer}
            viewportMode={viewportMode}
          />
        </div>

        {/* Panel de propiedades */}
        <PropertiesPanel
          selectedElement={selectedElement}
          onUpdateElement={updateElement}
          viewportMode={viewportMode}
        />
      </div>
    </div>
  );
}

export default Editor;
